import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Home, FileText, Mic, Radio, Video, Layout, 
  Sun, Moon, User, LogOut, Settings, 
  BarChart2, CreditCard, ChevronDown, Upload,
  Menu, X, PlayCircle, Headphones, Play,
  Users, UserPlus, Folder, Bookmark, Volume2, Search, Trash2, Link as LinkIcon, Download,
  ArrowLeft, Sparkles, Globe, ChevronLeft, ChevronRight, CheckCircle2, Loader2
} from 'lucide-react';

import MainLogo from './111EB892-C5CE-432F-A999-39427BCE488F.PNG';
import FaviconLogo from './A3AE2E2B-AA0A-4F1C-A8A1-0BD85F10B83F.PNG';

// ==========================================
// CONFIG & CONSTANTS
// ==========================================
// ==========================================
// CONFIG & CONSTANTS
// ==========================================
const API_BASE = "https://smelting-straddle-evaluate.ngrok-free.dev";
const isBetaAvailable = false;
const AUDIOBOOK_ACCEPT = [
  ".epub", ".pdf", ".docx", ".txt", ".md", ".html", ".xml", ".fdx", 
  ".mp3", ".wav", ".m4a", ".mp4", ".mkv", ".webm"
].join(",");

// ==========================================
// HELPER FUNCTIONS
// ==========================================
// ==========================================
// HELPER FUNCTIONS
// ==========================================
async function safeJsonFetch(url, options = {}) {
  // Ngrok bepul versiyasining ogohlantirish oynasini chetlab o'tish uchun maxsus header
  const customHeaders = {
    "ngrok-skip-browser-warning": "true",
    ...options.headers
  };

  const res = await fetch(url, { ...options, headers: customHeaders });
  const text = await res.text();
  let data = null;
  
  if (text) {
    try { data = JSON.parse(text); } catch { data = null; }
  }
  
  if (!res.ok) {
    const msg = data?.detail || data?.error || data?.message || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.responseText = text;
    throw err;
  }
  
  return data ?? {};
}

function getLocal(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function createEmptyFiles() {
  return { Dubs: [], TTS: [], Audiobooks: [], STT: [], Studio: [], Cloning: [] };
}

// ==========================================
// CUSTOM HOOKS
// ==========================================
function useJobPoll(url, enabled) {
  const [state, setState] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    if (!enabled || !url) return;

    const tick = async () => {
      try {
        const data = await safeJsonFetch(url);
        const normalized =
          data.status === "failed" ? "failed" : data.status === "completed" ? "completed" : "loading";

        setState({
          status: normalized,
          progress: data.progress || 0,
          message: data.message || "Processing...",
        });

        if (data.output_url) setDownloadUrl(`${API_BASE}${data.output_url}`);

        if (data.status === "completed" || data.status === "failed") {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (err) {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setState({ status: "failed", progress: 0, message: err.message || "Failed" });
      }
    };

    tick();
    pollRef.current = setInterval(tick, 2000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [url, enabled]);

  return [state, setState, downloadUrl, setDownloadUrl];
}

// ==========================================
// INLINED CHATBOT COMPONENT
// ==========================================
function Chatbot({ isDarkMode, t }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: t ? t("How can I help you today?") : "How can I help you today?" },
  ]);
  const [input, setInput] = useState("");

  const send = () => {
    if (!input.trim()) return;
    const user = { role: "user", text: input.trim() };
    const bot = { role: "assistant", text: "This is a local AI Assistant placeholder." };
    setMessages((prev) => [...prev, user, bot]);
    setInput("");
  };

  return (
    <div className={`flex h-full flex-col overflow-hidden rounded-3xl border ${isDarkMode ? "border-gray-700 bg-[#1e293b]" : "border-gray-200 bg-white"}`}>
      <div className="flex-1 space-y-3 overflow-auto p-4">
        {messages.map((m, idx) => (
          <div key={idx} className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "ml-auto bg-blue-600 text-white" : (isDarkMode ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-900")}`}>
            {m.text}
          </div>
        ))}
      </div>
      <div className={`border-t p-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
        <div className="flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask something..." className={`flex-1 rounded-2xl border px-4 py-3 outline-none ${isDarkMode ? "bg-[#0f172a] border-gray-600 text-white" : "bg-white border-gray-200 text-gray-900"}`} />
          <button onClick={send} className="rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition">Send</button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// MAIN APP COMPONENT
// ==========================================
const App = ({ onBackToLanding, onNavigateToPricing, user: initialUser = {} }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => getLocal('isDarkMode', false));
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('Home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeVideoModal, setActiveVideoModal] = useState(null);
  const [voicesTab, setVoicesTab] = useState('Explore');
  const [filesTab, setFilesTab] = useState('Dubs');



















   const storedUser = getLocal('userData', {});
  const normalizedUser = {
    name: initialUser?.name ?? storedUser?.name ?? localStorage.getItem('userName') ?? '',
    email: initialUser?.email ?? storedUser?.email ?? localStorage.getItem('userEmail') ?? '',
    credits: Number(
      initialUser?.credits ??
      storedUser?.credits ??
      localStorage.getItem('dubbigCredits') ??
      10000
    ),
    dubbing_minutes: Number(
      initialUser?.dubbing_minutes ??
      initialUser?.minutes ??
      storedUser?.dubbing_minutes ??
      storedUser?.minutes ??
      localStorage.getItem('dubbigMinutes') ??
      120
    ),
  };

  const [userName, setUserName] = useState(normalizedUser.name);
  const [userEmail, setUserEmail] = useState(normalizedUser.email);
  const [credits, setCredits] = useState(normalizedUser.credits);
  const [minutes, setMinutes] = useState(normalizedUser.dubbing_minutes);

  useEffect(() => {
    const nextUser = {
      name: initialUser?.name ?? storedUser?.name ?? localStorage.getItem('userName') ?? '',
      email: initialUser?.email ?? storedUser?.email ?? localStorage.getItem('userEmail') ?? '',
      credits: Number(
        initialUser?.credits ??
        storedUser?.credits ??
        localStorage.getItem('dubbigCredits') ??
        10000
      ),
      dubbing_minutes: Number(
        initialUser?.dubbing_minutes ??
        initialUser?.minutes ??
        storedUser?.dubbing_minutes ??
        storedUser?.minutes ??
        localStorage.getItem('dubbigMinutes') ??
        120
      ),
    };

    setUserName(nextUser.name);
    setUserEmail(nextUser.email);
    setCredits(nextUser.credits);
    setMinutes(nextUser.dubbing_minutes);
  }, [initialUser]);

  useEffect(() => {
    localStorage.setItem(
      'userData',
      JSON.stringify({
        name: userName,
        email: userEmail,
        credits,
        dubbing_minutes: minutes,
      })
    );
    localStorage.setItem('userName', userName);
    localStorage.setItem('userEmail', userEmail);
    localStorage.setItem('dubbigCredits', String(credits));
    localStorage.setItem('dubbigMinutes', String(minutes));
  }, [userName, userEmail, credits, minutes]);














  const [savedVoices, setSavedVoices] = useState(() => getLocal('savedVoices', []));
  const [myVoices, setMyVoices] = useState(() => getLocal('myVoices', []));
  const [myFiles, setMyFiles] = useState(() => getLocal('myFiles', createEmptyFiles()));
  const [deletedFiles, setDeletedFiles] = useState(() => getLocal('deletedFiles', []));
  const [deletedVoices, setDeletedVoices] = useState(() => getLocal('deletedVoices', []));
  const [requestsLog, setRequestsLog] = useState(() => getLocal('requestsLog', []));

  const [logoutModal, setLogoutModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, type: null, payload: null });
  const [mediaModal, setMediaModal] = useState({ isOpen: false, type: 'video', src: '', title: '' });
  const [nameModal, setNameModal] = useState({ isOpen: false, value: '' });

  const [usagePage, setUsagePage] = useState(0);
  const [deletedFilesPage, setDeletedFilesPage] = useState(0);
  const [deletedVoicesPage, setDeletedVoicesPage] = useState(0);

  const LOCAL_VIDEO_PATH = "file:///C:/Users/user/OneDrive/Pictures/Camera%20Roll/video11.mp4";
  const LOCAL_AUDIO_PATH = "file:///C:/Users/user/OneDrive/Pictures/Camera%20Roll/audio.mp3";

  const playLocalAudio = (e) => {
    if(e) e.stopPropagation();
    const audio = new Audio(LOCAL_AUDIO_PATH);
    audio.play().catch(err => {
      console.warn("Browser blocked local audio playback:", err);
      alert("Local audio file path: C:\\Users\\user\\OneDrive\\Pictures\\Camera Roll\\audio.mp3");
    });
  };

  const [isAppLangOpen, setIsAppLangOpen] = useState(false);
  const [appLang, setAppLang] = useState(() => getLocal('appLang', 'English'));
  const allLanguages = ['English', 'Uzbek', 'Russian', 'Spanish', 'Portuguese', 'Chinese', 'Arabic', 'Turkish', 'French', 'German', 'Japanese', 'Korean'];
  const [apiLanguages, setApiLanguages] = useState(allLanguages);
  const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem("userData")) || {};
  } catch (e) {
    return {};
  }
};

  // Persistence Effects
  useEffect(() => { localStorage.setItem('isDarkMode', isDarkMode.toString()); }, [isDarkMode]);
  useEffect(() => { localStorage.setItem('dubbigCredits', credits.toString()); }, [credits]);
  useEffect(() => { localStorage.setItem('dubbigMinutes', minutes.toString()); }, [minutes]);
  useEffect(() => { localStorage.setItem('userName', userName); }, [userName]);
  useEffect(() => { localStorage.setItem('savedVoices', JSON.stringify(savedVoices)); }, [savedVoices]);
  useEffect(() => { localStorage.setItem('myVoices', JSON.stringify(myVoices)); }, [myVoices]);
  useEffect(() => { localStorage.setItem('myFiles', JSON.stringify(myFiles)); }, [myFiles]);
  useEffect(() => { localStorage.setItem('deletedFiles', JSON.stringify(deletedFiles)); }, [deletedFiles]);
  useEffect(() => { localStorage.setItem('deletedVoices', JSON.stringify(deletedVoices)); }, [deletedVoices]);
  useEffect(() => { localStorage.setItem('requestsLog', JSON.stringify(requestsLog)); }, [requestsLog]);
  useEffect(() => { localStorage.setItem('appLang', appLang); }, [appLang]);

  // Fetch API Languages
  useEffect(() => {
    let mounted = true;
    fetch(`${API_BASE}/api/languages/`)
      .then((r) => r.json())
      .then((data) => {
        if (mounted && Array.isArray(data.languages) && data.languages.length) {
          setApiLanguages(data.languages);
        }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const translations = {
    'Home': ['Bosh sahifa', 'Главная', 'Inicio', 'Início', '首页', 'الرئيسية', 'Ana Sayfa', 'Accueil', 'Startseite', 'ホーム', '홈'],
    'Voices': ['Ovozlar', 'Голоса', 'Voces', 'Vozes', '声音', 'أصوات', 'Sesler', 'Voix', 'Stimmen', '音声', '음성'],
    'Dubbing AI': ['Dublyaj AI', 'Дубляж AI', 'Doblaje AI', 'Dublagem AI', '配音 AI', 'دبلجة AI', 'Dublaj AI', 'Doublage AI', 'Synchronisation AI', '吹き替え AI', '더빙 AI'],
    'Text to Speech': ['Matnni Ovozga', 'Текст в речь', 'Texto a Voz', 'Texto para Voz', '文本转语音', 'تحويل النص إلى كلام', 'Metinden Sese', 'Texte par Parole', 'Text-zu-Sprache', 'テキスト音声変換', '텍스트 음성 변환'],
    'Speech to Text': ['Ovozni Matnga', 'Речь в текст', 'Voz a Texto', 'Voz para Texto', '语音转文本', 'تحويل الكلام إلى نص', 'Sesten Metne', 'Parole par Texte', 'Sprache-zu-Text', '音声テキスト変換', '음성 텍스트 변환'],
    'Voice Cloning': ['Ovoz Klonlash', 'Клонирование голоса', 'Clonación de Voz', 'Clonagem de Voz', '声音克隆', 'استنساخ الصوت', 'Ses Klonlama', 'Clonage Vocal', 'Stimmenklonen', '音声クローニング', '음성 클로닝'],
    'Audiobook': ['Audiokitob', 'Аудиокнига', 'Audiolibro', 'Audiolivro', '有声读物', 'كتاب صوتي', 'Sesli Kitap', 'Livre Audio', 'Hörbuch', 'オーディオブック', '오디오북'],
    'Studio': ['Studiya', 'Студия', 'Estudio', 'Estúdio', '工作室', 'استوديو', 'Stüdyo', 'Studio', 'Studio', 'スタジオ', '스튜디오'],
    'Files': ['Fayllar', 'Файлы', 'Archivos', 'Arquivos', '文件', 'ملفات', 'Dosyalar', 'Fichiers', 'Dateien', 'ファイル', '파일'],
    'Saved': ['Saqlangan', 'Сохраненные', 'Guardados', 'Salvos', '已保存', 'محفوظ', 'Kaydedilenler', 'Enregistrés', 'Gespeichert', '保存済み', '저장됨'],
    'Settings': ['Sozlamalar', 'Настройки', 'Configuraciones', 'Configurações', '设置', 'إعدادات', 'Ayarlar', 'Paramètres', 'Einstellungen', '設定', '설정'],
    'Usage Analyze': ['Foydalanish tahlili', 'Анализ использования', 'Análisis de Uso', 'Análise de Uso', '使用分析', 'تحليل الاستخدام', 'Kullanım Analizi', 'Analyse d\'utilisation', 'Nutzungsanalyse', '使用状況', '사용량'],
    'Subscription': ['Obuna', 'Подписка', 'Suscripción', 'Assinatura', '订阅', 'اشتراك', 'Abonelik', 'Abonnement', 'Abonnement', 'サブスクリプション', '구독'],
    'Sign out': ['Chiqish', 'Выйти', 'Cerrar sesión', 'Sair', '登出', 'خروج', 'Çıkış yap', 'Se déconnecter', 'Abmelden', 'サインアウト', '로그아웃'],
    'Create a dub': ['Dublyaj yaratish', 'Создать дубляж', 'Crear doblaje', 'Criar dublagem', '创建配音', 'إنشاء دبلجة', 'Dublaj oluştur', 'Créer doublage', 'Synchronisation erstellen', '吹き替えを作成', '더빙 만들기'],
    'Generate Speech': ['Ovoz yaratish', 'Генерировать речь', 'Generar Voz', 'Gerar Voz', '生成语音', 'توليد الكلام', 'Ses Oluştur', 'Générer Parole', 'Sprache generieren', '音声を生成', '음성 생성'],
    'Start Cloning Process': ['Klonlashni boshlash', 'Начать клонирование', 'Iniciar Clonación', 'Iniciar Clonagem', '开始克隆', 'بدء الاستنساخ', 'Klonlamaya Başla', 'Démarrer Clonage', 'Klonen starten', 'クローン開始', '클로닝 시작'],
    'Generate Audiobook': ['Kitob yaratish', 'Генерировать книгу', 'Generar Audiolibro', 'Gerar Audiolivro', '生成有声读物', 'توليد كتاب', 'Kitap Oluştur', 'Générer Livre', 'Hörbuch generieren', 'オーディオブックを生成', '오디오북 생성'],
    'Start Transcription': ['Tarjimani boshlash', 'Начать транскрипцию', 'Iniciar Transcripción', 'Iniciar Transcrição', '开始转录', 'بدء النسخ', 'Transkripsiyona Başla', 'Démarrer Transcription', 'Transkription starten', '文字起こし開始', '전사 시작'],
    'Hello': ['Salom', 'Привет', 'Hola', 'Olá', '你好', 'مرحباً', 'Merhaba', 'Bonjour', 'Hallo', 'こんにちは', '안녕하세요'],
    'Welcome to Dubbig. Learn how to use our tools with the video guides below.': [
        'Dubbig-ga xush kelibsiz. Quyidagi video qo\'llanmalar yordamida vositalarimizdan qanday foydalanishni o\'rganing.',
        'Добро пожаловать в Dubbig. Узнайте, как использовать наши инструменты, с помощью видеоруководств ниже.',
        'Bienvenido a Dubbig. Aprenda a usar nuestras herramientas con las guías en video a continuación.',
        'Bem-vindo ao Dubbig. Aprenda a usar nossas ferramentas com os guias em vídeo abaixo.',
        '欢迎来到 Dubbig。通过下面的视频指南了解如何使用我们的工具。',
        'مرحبًا بك في Dubbig. تعرف على كيفية استخدام أدواتنا من خلال أدلة الفيديو أدناه.',
        'Dubbig\'e Hoş Geldiniz. Aşağıdaki video kılavuzlarıyla araçlarımızı nasıl kullanacağınızı öğrenin.',
        'Bienvenue sur Dubbig. Apprenez à utiliser nos outils avec les guides vidéo ci-dessous.',
        'Willkommen bei Dubbig. Erfahren Sie mit den folgenden Videoanleitungen, wie Sie unsere Tools verwenden.',
        'Dubbigへようこそ。以下のビデオガイドでツールの使い方を学びましょう。',
        'Dubbig에 오신 것을 환영합니다. 아래 비디오 가이드를 통해 도구 사용법을 알아보세요.'
    ],
    'Project Name': ['Loyiha nomi', 'Название проекта', 'Nombre del proyecto', 'Nome do proyecto', '项目名称', 'اسم المشروع', 'Proje Adı', 'Nom du projet', 'Projektname', 'プロジェクト名', '프로젝트 이름'],
    'Target Language': ['Maqsadli til', 'Целевой язык', 'Idioma de destino', 'Idioma de destino', '目标语言', 'اللغة الهدف', 'Hedef Dil', 'Langue cible', 'Zielsprache', 'ターゲット言語', '대상 언어'],
    'Source Link (Optional)': ['Manba havolasi (Ixtiyoriy)', 'Ссылка на источник (необязательно)', 'Enlace de origen (opcional)', 'Link de origem (opcional)', '源链接（可选）', 'رابط المصدر (اختياري)', 'Kaynak Bağlantısı (İsteğe bağlı)', 'Lien source (optionnel)', 'Quelllink (Optional)', 'ソースリンク（オプション）', '소스 링크 (선택 사항)'],
    'Or Upload Audio / Video': ['Yoki Audio/Video yuklang', 'Или загрузите аудио/видео', 'O subir audio/video', 'Ou enviar áudio/vídeo', '或上传音频/视频', 'أو تحميل صوت / فيديو', 'Veya Ses / Video Yükle', 'Ou télécharger audio/vidéo', 'Oder Audio/Video hochladen', 'またはオーディオ/ビデオをアップロード', '또는 오디오/비디오 업로드'],
    'Click or drag to upload': ['Yuklash uchun bosing yoki torting', 'Нажмите или перетащите для загрузки', 'Haz clic o arrastra para subir', 'Clique ou arraste para enviar', '点击或拖拽上传', 'انقر أو اسحب للتحميل', 'Yüklemek için tıklayın veya sürükleyin', 'Cliquez ou glissez pour télécharger', 'Klicken oder ziehen zum Hochladen', 'クリックまたはドラッグしてアップロード', '클릭하거나 드래그하여 업로드'],
    'Trending voices': ['Ommabop ovozlar', 'Популярные голоса', 'Voces en tendencia', 'Vozes em alta', '热门声音', 'أصوات شائعة', 'Trend sesler', 'Voix tendances', 'Beliebte Stimmen', 'トレンドの音声', '인기 음성'],
    'Explore': ['Kashf etish', 'Исследовать', 'Explorar', 'Explorar', '探索', 'استكشاف', 'Keşfet', 'Explorer', 'Erkunden', '探索する', '탐색'],
    'My Voices': ['Mening ovozlarim', 'Мои голоса', 'Mis voces', 'Minhas vozes', '我的声音', 'أصواتي', 'Seslerim', 'Mes voix', 'Meine Stimmen', 'マイボイス', '내 음성'],
    'Balance': ['Balans', 'Баланс', 'Saldo', 'Saldo', '余额', 'الرصيد', 'Bakiye', 'Solde', 'Guthaben', '残高', '잔액'],
    'minutes': ['daqiqa', 'минут', 'minutos', 'minutos', '分钟', 'دقائق', 'dakika', 'minutes', 'Minuten', '分', '분'],
    'Application language': ['Dastur tili', 'Язык приложения', 'Idioma de la aplicación', 'Idioma do aplicativo', '应用语言', 'لغة التطبيق', 'Uygulama dili', 'Langue de l\'application', 'Anwendungssprache', 'アプリの言語', '앱 언어'],
    'AI Assistant': ['AI Yordamchi', 'ИИ Ассистент', 'Asistente de IA', 'Assistente de IA', 'AI 助手', 'مساعد الذكاء الاصطناعي', 'Yapay Zeka Asistanı', 'Assistant IA', 'KI-Assistent', 'AIアシスタント', 'AI 어시스턴트'],
    'E-Mail Address': ['Elektron manzil', 'Электронная почта', 'Dirección de correo electrónico', 'Endereço de E-Mail', '电子邮件地址', 'عنوان البريد الإلكتروني', 'E-Posta Adresi', 'Adresse E-mail', 'E-Mail-Adresse', 'メールアドレス', '이메일 주소'],
    'Given Name': ['Ism', 'Имя', 'Nombre', 'Nome de batismo', '名字', 'الاسم الأول', 'Ad', 'Prénom', 'Vorname', '名', '이름'],
    'Update Given Name': ['Ismni yangilash', 'Обновить имя', 'Actualizar nombre', 'Atualizar Nome', '更新名字', 'تحديث الاسم الأول', 'Adı Güncelle', 'Mettre à jour le prénom', 'Vorname aktualisieren', '名前を更新', '이름 업데이트'],
    'Current Plan': ['Joriy reja', 'Текущий план', 'Plan actual', 'Plano Atual', '当前计划', 'الخطة الحالية', 'Mevcut Plan', 'Plan actuel', 'Aktueller Plan', '現在のプラン', '현재 요금제'],
    'Manage Subscription': ['Obunani boshqarish', 'Управление подпиской', 'Administrar suscripción', 'Gerenciar Assinatura', '管理订阅', 'إدارة الاشتراك', 'Aboneliği Yönet', 'Gérer l\'abonnement', 'Abonnement verwalten', 'サブスクリプションを管理', '구독 관리'],
    'Model': ['Model', 'Модель', 'Modelo', 'Modelo', '模型', 'نموذج', 'Model', 'Modèle', 'Modell', 'モデル', '모델'],
    'Select Speaker': ['Ovozni tanlang', 'Выберите спикера', 'Seleccionar altavoz', 'Selecionar Locutor', '选择发音人', 'اختر المتحدث', 'Konuşmacı Seç', 'Sélectionner l\'orateur', 'Sprecher auswählen', '話者を選択', '연사 선택'],
    'Language': ['Til', 'Язык', 'Idioma', 'Idioma', '语言', 'لغة', 'Dil', 'Langue', 'Sprache', '言語', '언어'],
    'Convert Speech to Text': ['Ovozni matnga o\'giring', 'Преобразовать речь в текст', 'Convertir voz a texto', 'Converter fala em texto', '将语音转换为文本', 'تحويل الكلام إلى نص', 'Sesi Metne Dönüştür', 'Convertir la parole en texte', 'Sprache in Text umwandeln', '音声をテキストに変換', '음성을 텍스트로 변환'],
    'Transcription Name': ['Transkripsiya nomi', 'Название транскрипции', 'Nombre de la transcripción', 'Nome da Transcrição', '转录名称', 'اسم النسخ', 'Transkripsiyon Adı', 'Nom de la transcription', 'Transkriptionsname', '文字起こし名', '전사 이름'],
    'Paste YouTube URL': ['YouTube havolasini qo\'ying', 'Вставьте ссылку YouTube', 'Pegar URL de YouTube', 'Cole o URL do YouTube', '粘贴 YouTube URL', 'لصق رابط يوتيوب', 'YouTube URL\'sini yapıştır', 'Coller l\'URL YouTube', 'YouTube-URL einfügen', 'YouTube URLを貼り付け', 'YouTube URL 붙여넣기'],
    'Voice Name': ['Ovoz nomi', 'Название голоса', 'Nombre de la voz', 'Nome da Voz', '声音名称', 'اسم الصوت', 'Ses Adı', 'Nom de la voix', 'Stimmenname', '音声名', '음성 이름'],
    'Native Language': ['Ona tili', 'Родной язык', 'Idioma nativo', 'Idioma Nativo', '母语', 'اللغة الأم', 'Ana Dil', 'Langue maternelle', 'Muttersprache', '母国語', '모국어'],
    'Audiobook Name': ['Audiokitob nomi', 'Название аудиокниги', 'Nombre del audiolibro', 'Nome do Audiolivro', '有声读物名称', 'اسم الكتاب الصوتي', 'Sesli Kitap Adı', 'Nom du livre audio', 'Hörbuchname', 'オーディオブック名', '오디오북 이름'],
    'Upload Document': ['Hujjatni yuklang', 'Загрузить документ', 'Subir documento', 'Carregar Documento', '上传文档', 'تحميل المستند', 'Belge Yükle', 'Télécharger le document', 'Dokument hochladen', 'ドキュメントをアップロード', '문서 업로드'],
    'Dubs': ['Dublyajlar', 'Дубляжи', 'Doblajes', 'Dublagens', '配音', 'دبلجات', 'Dublajlar', 'Doublages', 'Synchronisationen', '吹き替え', '더빙'],
    'TTS': ['TTS', 'TTS', 'TTS', 'TTS', 'TTS', 'تحويل النص إلى كلام', 'TTS', 'TTS', 'TTS', 'TTS', 'TTS'],
    'Audiobooks': ['Audiokitoblar', 'Аудиокниги', 'Audiolibros', 'Audiolivros', '有声读物', 'كتب صوتية', 'Sesli Kitaplar', 'Livres audio', 'Hörbücher', 'オーディオブック', '오디오북'],
    'STT': ['STT', 'STT', 'STT', 'STT', 'STT', 'تحويل الكلام إلى نص', 'STT', 'STT', 'STT', 'STT', 'STT'],
    'Preserve the original speaker\'s voice': ['Asl so\'zlovchining ovozini saqlab qoling', 'Сохранить голос оригинального диктора', 'Conservar la voz del orador original', 'Preservar a voz do original', '保留原始发音人的声音', 'الحفاظ على صوت المتحدث الأصلي', 'Orijinal konuşmacının sesini koru', 'Préserver la voix originale', 'Stimme des Originalsprechers beibehalten', '元の話者の声を保持する', '원래 화자의 음성 보존'],
    'e.g. My Custom Voice': ['masalan, Mening Ovozim', 'напр., Мой голос', 'p. ej. Mi Voz', 'ex. Minha Voz', '例如：我的自定义声音', 'على سبيل المثال، صوتي', 'örn. Benim Özel Sesim', 'ex. Ma Voix', 'z.B. Meine Stimme', '例: 私のカスタム音声', '예: 내 맞춤 음성'],
    'No files found.': ['Hech qanday fayl topilmadi.', 'Файлы не найдены.', 'No se encontraron archivos.', 'Nenhum arquivo encontrado.', '未找到文件。', 'لم يتم العثور على ملفات.', 'Dosya bulunamadı.', 'Aucun fichier trouvé.', 'Keine gelöschten Dateien.', 'ファイルが見つかりません。', '파일을 찾을 수 없습니다.'],
    'You haven\'t saved any voices yet. Go to Explore to save some.': ['Siz hali hech qanday ovoz saqlamadingiz. Kashf etish bo\'limidan saqlang.', 'Вы еще не сохранили ни одного голоса. Перейдите в раздел Исследовать.', 'Aún no has guardado ninguna voz. Ve a Explorar.', 'Você ainda não salvou nenhuma voz. Vá para Explorar.', '您尚未保存任何声音。前往“探索”保存。', 'لم تقم بحفظ أي أصوات بعد. انتقل إلى "استكشاف".', 'Henüz hiç ses kaydetmediniz. Kaydetmek için Keşfet\'e gidin.', 'Vous n\'avez pas encore enregistré de voix. Allez dans Explorer.', 'Sie haben noch keine Stimmen gespeichert. Gehen Sie zu Erkunden.', 'まだ音声を保存していません。探索に行ってください。', '아직 음성을 저장하지 않았습니다. 탐색으로 이동하세요.'],
    'Delete Entire Account': ['Butun hisobni o\'chirish', 'Удалить весь аккаунт', 'Eliminar cuenta completa', 'Excluir Conta Inteira', '删除整个账户', 'حذف الحساب بالكامل', 'Tüm Hesabı Sil', 'Supprimer le compte entier', 'Gesamtes Konto löschen', 'アカウント全体を削除', '전체 계정 삭제'],
    'Permanently delete your entire account across all workspaces.': ['Barcha ish joylaridagi butun hisobingizni o\'chirib tashlang.', 'Навсегда удалить весь ваш аккаунт во всех рабочих областях.', 'Elimina permanentemente tu cuenta completa en todos los espacios de trabajo.', 'Exclua permanentemente sua conta em todos os espaços de trabalho.', '永久删除您在所有工作区中的整个帐户。', 'احذف حسابك بالكامل نهائيًا عبر جميع مساحات العمل.', 'Tüm çalışma alanlarındaki hesabınızı kalıcı olarak silin.', 'Supprimez définitivement votre compte entier.', 'Löschen Sie Ihr gesamtes Konto dauerhaft.', 'すべてのアカウントを完全に削除します。', '모든 계정을 영구적으로 삭제합니다.'],
    'Delete Account': ['Hisobni o\'chirish', 'Удалить аккаунт', 'Eliminar cuenta', 'Excluir Conta', '删除账户', 'حذف الحساب', 'Hesabı Sil', 'Supprimer le compte', 'Konto löschen', 'アカウントを削除', '계정 삭제'],
    'Recently Deleted': ["Yaqinda o'chirilganlar", "Недавно удаленные", "Eliminados recientemente", "Excluídos Recentemente", "最近删除", "المحذوفة مؤخرًا", "Son Silinenler", "Supprimés récemment", "Kürzlich gelöscht", "最近削除された", "최근 삭제됨"],
    'Speed': ['Tezlik', 'Скорость', 'Velocidad', 'Velocidade', '速度', 'سرعة', 'Hız', 'Vitesse', 'Geschwindigkeit', '速度', '속도'],
    'Your Usage & Credit Limit Details': ["Foydalanish va Kredit Limitingiz Tafsilotlari", "Сведения об использовании и кредитном лимите", "Detalles de su uso y límite de crédito", "Detalhes de uso e limite de crédito", "您的使用情况和信用额度详情", "تفاصيل الاستخدام وحد الائتمان الخاص بك", "Kullanım ve Kredi Limiti Ayrıntılarınız", "Vos détails d'utilisation et de limite de crédit", "Ihre Nutzungs- und Kreditlimitdetails", "利用状況とクレジット制限の詳細", "사용 및 크레딧 한도 세부 정보"],
    'Limit Name': ["Limit Nomi", "Название лимита", "Nombre del límite", "Nome do Limite", "限制名称", "اسم الحد", "Limit Adı", "Nom de la limite", "Limitname", "制限名", "한도 이름"],
    'Usage': ["Foydalanish", "Использование", "Uso", "Uso", "使用情况", "الاستخدام", "Kullanım", "Utilisation", "Nutzung", "利用状況", "사용량"],
    'Limit': ["Limit", "Лимит", "Límite", "Limite", "限制", "حد", "Limit", "Limite", "Limit", "制限", "한도"],
    'Workspace': ["Ish joyi", "Рабочая область", "Espacio de trabajo", "Espaço de trabalho", "工作区", "مساحة العمل", "Çalışma Alanı", "Espace de travail", "Arbeitsbereich", "ワークスペース", "작업 공간"],
    'Remaining': ["Qolgan", "Остаток", "Restante", "Restante", "剩余", "المتبقي", "Kalan", "Restant", "Verbleibend", "残り", "남은"],
    'Based on the most restrictive limit': ["Eng qat'iy limitga asoslangan", "На основе самого строгого лимита", "Basado en el límite más restrictivo", "Com base no limite mais restritivo", "基于最严格的限制", "بناءً على الحد الأكثر تقييدًا", "En kısıtlayıcı limite göre", "Basé sur la limite la plus restrictive", "Basierend auf dem strengsten Limit", "最も厳しい制限に基づく", "가장 엄격한 한도 기준"],
    'Request Logs': ["So'rovlar jurnali", "Журналы запросов", "Registros de solicitudes", "Logs de Solicitações", "请求日志", "سجلات الطلبات", "İstek Günlükleri", "Journaux de requêtes", "Anfrageprotokolle", "リクエストログ", "요청 로그"],
    'All Requests': ["Barcha so'rovlar", "Все запросы", "Todas las solicitudes", "Todas as Solicitações", "所有请求", "جميع الطلبات", "Tüm İstekler", "Toutes les requêtes", "Alle Anfragen", "すべてのリクエスト", "모든 요청"],
    'Are you sure you want to delete this?': ['Buni o\'chirishga ishonchingiz komilmi?', 'Вы уверены, что хотите удалить это?', '¿Estás seguro de que quieres eliminar esto?', 'Tem certeza de que deseja excluir isso?', '您确定要删除这个吗？', 'هل أنت متأكد أنك تريد حذف هذا؟', 'Bunu silmek istediğinizden emin misiniz?', 'Voulez-vous vraiment supprimer ceci ?', 'Sind Sie sicher, dass Sie dies löschen möchten?', 'これを削除してもよろしいですか？', '이것을 삭제하시겠습니까?'],
    'Yes': ['Ha', 'Да', 'Sí', 'Sim', '是', 'نعم', 'Evet', 'Oui', 'Ja', 'はい', '예'],
    'No': ['Yo\'q', 'Нет', 'No', 'Não', '否', 'لا', 'Hayır', 'Non', 'Nein', 'いいえ', '아니요'],
    'Remove': ['Olib tashlash', 'Удалить', 'Eliminar', 'Remover', '移除', 'إزالة', 'Kaldır', 'Retirer', 'Entfernen', '削除する', '제거'],
    'Recovery to use': ['Qayta tiklash', 'Восстановить', 'Recuperar para usar', 'Recuperar para uso', '恢复使用', 'استعادة للاستخدام', 'Kullanım için kurtar', 'Récupérer', 'Zur Verwendung wiederherstellen', '復元して使用', '사용 복구'],
    'Voice Clone': ['Klon Ovoz', 'Клон голоса', 'Clon de voz', 'Clone de Voz', '声音克隆', 'استنساخ الصوت', 'Ses Klonu', 'Clone vocal', 'Stimmenklon', '音声クローン', '음성 클론'],
    'Library Voice': ['Kutubxona Ovozi', 'Голос из библиотеки', 'Voz de la biblioteca', 'Voz da Biblioteca', '库声音', 'صوت المكتبة', 'Kütüphane Sesi', 'Voix de la bibliothèque', 'Bibliotheksstimme', 'ライブラリ音声', '라이브러리 음성'],
    'File generating...': ['Fayl yaratilmoqda...', 'Создание файла...', 'Generando archivo...', 'Gerando arquivo...', '正在生成文件...', 'جاري إنشاء الملف...', 'Dosya oluşturuluyor...', 'Génération du fichier...', 'Datei wird generiert...', 'ファイル生成中...', '파일 생성 중...'],
    'Completed!': ['Tugallandi!', 'Завершено!', '¡Completado!', 'Concluído!', '已完成！', 'مكتمل!', 'Tamamlandı!', 'Terminé !', 'Abgeschlossen!', '完了しました！', '완료!'],
    'Create another': ['Yana yaratish', 'Создать еще', 'Crear otro', 'Criar outro', '创建另一个', 'إنشاء آخر', 'Başka bir tane oluştur', 'Créer un autre', 'Ein weiteres erstellen', '別のを作成', '다른 것 만들기'],
    'Go to ': ['O\'tish: ', 'Перейти в ', 'Ir a ', 'Ir para ', '转到 ', 'الذهاب إلى ', 'Git ', 'Aller à ', 'Gehe zu ', 'に移動 ', '이동 '],
    'Success': ['Muvaffaqiyatli', 'Успешно', 'Éxito', 'Sucesso', '成功', 'نجاح', 'Başarılı', 'Succès', 'Erfolg', '成功', '성공'],
    'How can I help you today?': ['Bugun sizga qanday yordam bera olaman?', 'Чем я могу вам помочь сегодня?', '¿Cómo puedo ayudarte hoy?', 'Como posso ajudá-lo hoje?', '今天我能帮您什么？', 'كيف يمكنني مساعدتك اليوم؟', 'Bugün size nasıl yardımcı olabilirim?', 'Comment puis-je vous aider aujourd\'hui ?', 'Wie kann ich Ihnen heute helfen?', '今日はどのようなご用件ですか？', '오늘 무엇을 도와드릴까요?'],
    'Back': ['Orqaga', 'Назад', 'Volver', 'Voltar', '返回', 'رجوع', 'Geri', 'Retour', 'Zurück', '戻る', '뒤로'],
    'Save': ['Saqlash', 'Сохранить', 'Guardar', 'Salvar', '保存', 'حفظ', 'Kaydet', 'Enregistrer', 'Speichern', '保存', '저장'],
    'Playing...': ['Ijro etilmoqda...', 'Воспроизведение...', 'Reproduciendo...', 'Reproduzindo...', '播放中...', 'جار التشغيل...', 'Oynatılıyor...', 'Lecture...', 'Wird abgespielt...', '再生中...', '재생 중...'],
    'No deleted files.': ['O\'chirilgan fayllar yo\'q.', 'Нет удаленных файлов.', 'No hay archivos eliminados.', 'Nenhum arquivo excluído.', '没有删除的文件。', 'لا توجد ملفات محذوفة.', 'Silinmiş dosya yok.', 'Aucun fichier supprimé.', 'Keine gelöschten Dateien.', '削除されたファイルはありません。', '삭제된 파일이 없습니다.'],
    'No deleted voices.': ['O\'chirilgan ovozlar yo\'q.', 'Нет удаленных голосов.', 'No hay voces eliminadas.', 'Nenhuma voz excluída.', '没有删除的声音。', 'لا توجد أصوات محذوفة.', 'Silinmiş ses yok.', 'Aucune voix supprimée.', 'Keine gelöschten Stimmen.', '削除された音声はありません。', '삭제된 음성이 없습니다.'],
    'Start creating': ['Yaratishni boshlash', 'Начать создание', 'Empezar a crear', 'Começar a criar', '开始创建', 'البدء في الإنشاء', 'Oluşturmaya başla', 'Commencer à créer', 'Erstellung starten', '作成を開始', '만들기 시작'],
    'Are you sure you want to sign out?': ['Haqiqatan ham tizimdan chiqmoqchimisiz?', 'Вы уверены, что хотите выйти?', '¿Estás seguro de que quieres cerrar sesión?', 'Tem certeza de que deseja sair?', '您确定要退出吗？', 'هل أنت متأكد أنك تريد تسجيل الخروج؟', 'Çıkış yapmak istediğinizden emin misiniz?', 'Êtes-vous sûr de vouloir vous déconnecter ?', 'Möchten Sie sich wirklich abmelden?', '本当にログアウトしますか？', '로그아웃하시겠습니까?'],
    'Upgrade Plan': ['Rejani yangilash', 'Обновить план', 'Actualizar plan', 'Atualizar plano', '升级计划', 'ترقية الخطة', 'Planı Yükselt', 'Mettre à niveau le plan', 'Plan aktualisieren', 'プランをアップグレード', '요금제 업그레이드'],
    'Free': ['Bepul', 'Бесплатно', 'Gratis', 'Livre', '免费', 'مجاني', 'Ücretsiz', 'Gratuit', 'Kostenlos', '無料', '무료'],
    'Benefits': ['Imkoniyatlari', 'Преимущества', 'Beneficios', 'Benefícios', '好处', 'الفوائد', 'Avantajlar', 'Avantages', 'Vorteile', '利点', '혜택'],
    '10,000 credits/mo': ['10,000 kredit/oy', '10,000 кредитов/мес', '10,000 créditos/mes', '10.000 créditos/mês', '10,000 积分/月', '10,000 رصيد/شهر', '10.000 kredi/ay', '10 000 crédits/mois', '10.000 Credits/Monat', '10,000 クレジット/月', '월 10,000 크레딧'],
    '10 mins Dubbing AI/mo': ['Dublyaj AI 10 daqiqa/oy', '10 мин. Дубляж AI/мес', '10 min. Doblaje AI/mes', '10 min. Dublagem AI/mês', '10 分钟 配音 AI/月', '10 دقائق دبلجة الذكاء الاصطناعي/شهر', '10 dk Dublaj AI/ay', '10 min Doublage IA/mois', '10 Min. KI-Synchronisation/Monat', '10分 AI吹き替え/月', '월 10분 더빙 AI'],
    '80+ languages': ['80+ til', '80+ языков', '80+ idiomas', '80+ idiomas', '80+ 种语言', '80+ لغة', '80+ dil', '80+ langues', '80+ Sprachen', '80以上の言語', '80개 이상의 언어'],
    'Standard audio quality': ['Standart audio', 'Стандартное аудио', 'Calidad de audio estándar', 'Qualidade de áudio padrão', '标准音频质量', 'جودة صوت قياسية', 'Standart ses kalitesi', 'Qualité audio standard', 'Standard-Audioqualität', '標準オーディオ品質', '표준 오디오 품질']
  };

  const t = (text) => {
    if (appLang === 'English') return text;
    const langIndex = ['Uzbek', 'Russian', 'Spanish', 'Portuguese', 'Chinese', 'Arabic', 'Turkish', 'French', 'German', 'Japanese', 'Korean'].indexOf(appLang);
    return (translations[text] && translations[text][langIndex]) ? translations[text][langIndex] : text;
  };

  const changeAppLanguage = (lang) => {
    setAppLang(lang);
    setIsAppLangOpen(false);
  };

  const confirmSignOut = () => {
    localStorage.removeItem('isRegistered');
    setLogoutModal(false);
    if (onBackToLanding) onBackToLanding();
  };

  // ==========================================
  // REAL API STATES & HANDLERS
  // ==========================================
  
  // TTS State
  const [ttsSpeed, setTtsSpeed] = useState('1.0x');
  const [ttsEnergy, setTtsEnergy] = useState(1.0);
  const [ttsTitle, setTtsTitle] = useState('');
  const [ttsText, setTtsText] = useState('');
  const [isTtsVoiceOpen, setIsTtsVoiceOpen] = useState(false);
  const [ttsSelectedVoice, setTtsSelectedVoice] = useState('Leon - Soothing Grounded');
  const [isTtsLangOpen, setIsTtsLangOpen] = useState(false);
  const [ttsLang, setTtsLang] = useState('English');
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsAudio, setTtsAudio] = useState("");

  // Voice Cloning State
  const [cloningFile, setCloningFile] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [isCloneLangOpen, setIsCloneLangOpen] = useState(false);
  const [cloneLang, setCloneLang] = useState('English');
  const [cloneJobId, setCloneJobId] = useState(null);

  // Dubbing State
  const [dubName, setDubName] = useState('');
  const [dubLink, setDubLink] = useState('');
  const [dubVoiceCloning, setDubVoiceCloning] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDubbingLangOpen, setIsDubbingLangOpen] = useState(false);
  const [dubbingLang, setDubbingLang] = useState('Select language');
  const [dubJobId, setDubJobId] = useState(null);

  // STT State
  const [sttName, setSttName] = useState(''); 
  const [isSttLangOpen, setIsSttLangOpen] = useState(false);
  const [sttLang, setSttLang] = useState('English');
  const [sttFile, setSttFile] = useState(null);
  const [sttLink, setSttLink] = useState('');
  const [sttJobId, setSttJobId] = useState(null);
  const [sttTranscript, setSttTranscript] = useState("");

  // Audiobook State
  const [audiobookName, setAudiobookName] = useState(''); 
  const [isAudiobookLangOpen, setIsAudiobookLangOpen] = useState(false);
  const [audiobookLang, setAudiobookLang] = useState('English');
  const [audiobookFile, setAudiobookFile] = useState(null);
  const [isAudiobookVoiceOpen, setIsAudiobookVoiceOpen] = useState(false);
  const [audiobookSelectedVoice, setAudiobookSelectedVoice] = useState('Leon - Soothing Grounded');
  const [abJobId, setAbJobId] = useState(null);

  const [settingsView, setSettingsView] = useState('main'); 
  const [usageFilter, setUsageFilter] = useState('All');
  const [aiAssistantHistory, setAiAssistantHistory] = useState(['Home']);

  // Log function mapping
  const runLog = (type, costType, costValue) => {
    const newLog = { 
      id: Date.now(), 
      type, 
      date: new Date().toLocaleDateString(), 
      time: new Date().toLocaleTimeString(), 
      cost: `${costValue} ${costType}` 
    };
    setRequestsLog((prev) => [newLog, ...prev]);
    if (costType === "minutes") setMinutes((prev) => Math.max(0, prev - costValue));
    if (costType === "credits") setCredits((prev) => Math.max(0, prev - costValue));
  };

  // Pollers
  const pollDubUrl = dubJobId ? `${API_BASE}/api/dub/status/${dubJobId}/` : null;
  const pollCloneUrl = cloneJobId ? `${API_BASE}/api/voice-clone/status/${cloneJobId}/` : null;
  const pollAbUrl = abJobId ? `${API_BASE}/api/ab/status/${abJobId}/` : null;
  const pollSttUrl = sttJobId ? `${API_BASE}/api/stt/status/${sttJobId}/` : null;

  const [dubState, setDubState, dubDownloadUrl, setDubDownloadUrl] = useJobPoll(pollDubUrl, !!dubJobId);
  const [cloneState, setCloneState, cloneDownloadUrl, setCloneDownloadUrl] = useJobPoll(pollCloneUrl, !!cloneJobId);
  const [abState, setAbState, abDownloadUrl, setAbDownloadUrl] = useJobPoll(pollAbUrl, !!abJobId);
  const [sttState, setSttState, sttDownloadUrl, setSttDownloadUrl] = useJobPoll(pollSttUrl, !!sttJobId);

  // Job Completers
  useEffect(() => {
    if (dubState && dubState.status === "completed" && myFiles.Dubs.length) {
      const updated = [...myFiles.Dubs];
      const lastIndex = updated.length - 1;
      if (updated[lastIndex] && updated[lastIndex].status !== "done") {
        updated[lastIndex] = { ...updated[lastIndex], status: "done", download: dubDownloadUrl };
        setMyFiles((prev) => ({ ...prev, Dubs: updated }));
      }
    }
  }, [dubState, dubDownloadUrl]);

  useEffect(() => {
    if (abState && abState.status === "completed" && myFiles.Audiobooks.length) {
      const updated = [...myFiles.Audiobooks];
      const lastIndex = updated.length - 1;
      if (updated[lastIndex] && updated[lastIndex].status !== "done") {
        updated[lastIndex] = { ...updated[lastIndex], status: "done", download: abDownloadUrl };
        setMyFiles((prev) => ({ ...prev, Audiobooks: updated }));
      }
    }
  }, [abState, abDownloadUrl]);

  useEffect(() => {
    if (sttState && sttState.status === "completed" && myFiles.STT.length) {
      const updated = [...myFiles.STT];
      const lastIndex = updated.length - 1;
      if (updated[lastIndex] && updated[lastIndex].status !== "done") {
        updated[lastIndex] = { ...updated[lastIndex], status: "done", download: sttDownloadUrl };
        setMyFiles((prev) => ({ ...prev, STT: updated }));
      }
    }
  }, [sttState, sttDownloadUrl]);

  useEffect(() => {
    if (cloneState && cloneState.status === "completed") {
      const voice = { id: Date.now(), name: cloneName, desc: "Voice Clone", color: "from-blue-500 to-cyan-500" };
      setMyVoices((prev) => [...prev, voice]);
      setDeletedVoices((prev) => prev.filter((v) => v.id !== voice.id));
      setSavedVoices((prev) => [...prev, voice]);
      setMyFiles((prev) => ({
        ...prev,
        Cloning: [...(prev.Cloning || []), { id: Date.now(), name: cloneName, type: "Clone", date: new Date().toLocaleDateString(), status: "done", download: cloneDownloadUrl }],
      }));
    }
  }, [cloneState, cloneDownloadUrl]);


  // API Triggers
  const handleCreateDub = async () => {
    try {
      setDubState({ status: "loading", progress: 1, message: "Creating job..." });
      setDubDownloadUrl("");
      setDubJobId(null);
      const formData = new FormData();
      formData.append("title", dubName);
      formData.append("target_language", dubbingLang || "English");
      if (dubLink.trim()) formData.append("source_link", dubLink.trim());
      if (uploadedFile) formData.append("source_file", uploadedFile);
      const data = await safeJsonFetch(`${API_BASE}/api/dub/create/`, { method: "POST", body: formData });
      setDubJobId(data.job_id);
      runLog("Dubbing AI", "minutes", Math.floor(Math.random() * 6) + 5);
      setMyFiles((prev) => ({
        ...prev,
        Dubs: [...prev.Dubs, { id: Date.now(), name: dubName, type: uploadedFile ? "File" : "Link", date: new Date().toLocaleDateString(), status: "processing" }],
      }));
    } catch (err) {
      setDubState({ status: "failed", progress: 0, message: err.message || "Failed" });
    }
  };

  const handleGenerateTTS = async () => {
    try {
      setTtsLoading(true);
      setTtsAudio("");
      const formData = new FormData();
      formData.append("text", ttsText);
      formData.append("language", ttsLang || "English");
      formData.append("speed", String(parseFloat(ttsSpeed)));
      formData.append("energy", String(ttsEnergy));

      const data = await safeJsonFetch(`${API_BASE}/api/tts/`, { method: "POST", body: formData });
      if (data.file) {
        setTtsAudio(`${API_BASE}${data.file}`);
        setMyFiles((prev) => ({
          ...prev,
          TTS: [...prev.TTS, { id: Date.now(), name: ttsTitle || ttsText.slice(0, 24) || "TTS Audio", type: "Audio", date: new Date().toLocaleDateString(), download: `${API_BASE}${data.file}` }],
        }));
        runLog("Text to Speech", "credits", Math.floor(Math.random() * 301) + 200);
      }
    } catch (err) {
      alert(err.message || "TTS failed");
    } finally {
      setTtsLoading(false);
    }
  };

  const handleCreateClone = async () => {
    try {
      setCloneState({ status: "loading", progress: 1, message: "Creating clone..." });
      setCloneDownloadUrl("");
      setCloneJobId(null);
      const formData = new FormData();
      formData.append("name", cloneName);
      formData.append("language", cloneLang);
      if (cloningFile) formData.append("source_file", cloningFile);
      const data = await safeJsonFetch(`${API_BASE}/api/voice-clone/create/`, { method: "POST", body: formData });
      setCloneJobId(data.job_id);
      runLog("Voice Cloning", "credits", 0);
    } catch (err) {
      setCloneState({ status: "failed", progress: 0, message: err.message || "Failed" });
    }
  };

  const handleGenerateSTT = async () => {
    try {
      setSttState({ status: "loading", progress: 1, message: "Creating STT..." });
      setSttTranscript("");
      setSttDownloadUrl("");
      setSttJobId(null);
      const formData = new FormData();
      formData.append("title", sttName);
      formData.append("target_language", sttLang || "English");
      if (sttFile) formData.append("source_file", sttFile);
      // Backend STT logic might not support source_link, but appending just in case
      if (sttLink) formData.append("source_link", sttLink);
      
      const data = await safeJsonFetch(`${API_BASE}/api/stt/create/`, { method: "POST", body: formData });
      setSttJobId(data.job_id);
      runLog("Speech to Text", "credits", Math.floor(Math.random() * 301) + 200);
      setMyFiles((prev) => ({
        ...prev,
        STT: [...prev.STT, { id: Date.now(), name: sttName, type: sttFile ? "File" : "Link", date: new Date().toLocaleDateString(), status: "processing" }],
      }));
    } catch (err) {
      setSttState({ status: "failed", progress: 0, message: err.message || "Failed" });
    }
  };

  const handleCreateAudiobook = async () => {
    try {
      setAbState({ status: "loading", progress: 1, message: "Creating audiobook..." });
      setAbDownloadUrl("");
      setAbJobId(null);
      const formData = new FormData();
      formData.append("title", audiobookName);
      formData.append("target_language", audiobookLang || "English");
      if (audiobookFile) formData.append("source_file", audiobookFile);
      formData.append("speed", "1.0");
      formData.append("energy", "1.0");

      const data = await safeJsonFetch(`${API_BASE}/api/ab/create/`, { method: "POST", body: formData });
      setAbJobId(data.job_id);
      runLog("Audiobook", "credits", Math.floor(Math.random() * 301) + 200);
      setMyFiles((prev) => ({
        ...prev,
        Audiobooks: [...prev.Audiobooks, { id: Date.now(), name: audiobookName, type: "Document", date: new Date().toLocaleDateString(), status: "processing" }],
      }));
    } catch (err) {
      setAbState({ status: "failed", progress: 0, message: err.message || "Failed" });
    }
  };

  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [isDarkMode]);

  const mainMenus = [
    { id: 'Home', name: t('Home'), icon: <Home size={20} /> },
    { id: 'Voices', name: t('Voices'), icon: <Users size={20} /> },
    { id: 'Dubbing AI', name: t('Dubbing AI'), icon: <Video size={20} /> },
    { id: 'Text to Speech', name: t('Text to Speech'), icon: <Mic size={20} /> },
    { id: 'Speech to Text', name: t('Speech to Text'), icon: <Radio size={20} /> },
    { id: 'Voice Cloning', name: t('Voice Cloning'), icon: <UserPlus size={20} /> },
    { id: 'Audiobook', name: t('Audiobook'), icon: <Headphones size={20} /> },
    { id: 'Studio', name: t('Studio'), icon: <Layout size={20} /> },
  ];

  const bottomMenus = [
    { id: 'Files', name: t('Files'), icon: <Folder size={20} /> },
    { id: 'Saved', name: t('Saved'), icon: <Bookmark size={20} /> },
  ];

  const dummyVoices = [
    { id: 1, name: 'Ben - Effortless and Casual', desc: 'Conversational', color: 'from-red-400 to-pink-500' },
    { id: 2, name: 'Laura - Calm and Smooth', desc: 'Narration', color: 'from-purple-400 to-fuchsia-500' },
    { id: 3, name: 'Leon - Soothing Grounded', desc: 'Educational', color: 'from-green-400 to-emerald-500' },
    { id: 4, name: 'Kerstin - Nice and Lively', desc: 'Narration', color: 'from-cyan-400 to-blue-500' },
    { id: 5, name: 'Sully - Mature, Deep', desc: 'Sully - Deep mature American male', color: 'from-gray-700 to-gray-900' },
    { id: 6, name: 'Katherine - Calm Luxury', desc: 'Katherine delivers a smooth, sophisticated...', color: 'from-yellow-400 to-orange-500' },
    { id: 7, name: 'Adam - Trustworthy', desc: 'Deep, engaging and calm', color: 'from-blue-400 to-indigo-500' },
    { id: 8, name: 'Sofia - Bright', desc: 'Energetic, youthful voice', color: 'from-pink-400 to-rose-500' },
    { id: 9, name: 'Yuki - Gentle', desc: 'Soft-spoken and relaxing', color: 'from-teal-400 to-emerald-500' }
  ];

  const uniqueTTSVoices = [];
  const seenIds = new Set();
  [...dummyVoices.slice(0, 3), ...savedVoices].forEach(v => {
    if (!seenIds.has(v.id)) { seenIds.add(v.id); uniqueTTSVoices.push(v); }
  });

  const localizedVideoGuides = {
    'English': [
      { id: 'Text to Speech', title: 'How to use Text to Speech', color: 'from-blue-500 to-cyan-400', date: '4/4/26, 10:00' },
      { id: 'Speech to Text', title: 'How to use Speech to Text', color: 'from-purple-500 to-pink-400', date: '4/6/26, 15:30' },
      { id: 'Dubbing AI', title: 'Dubbing AI Guide', color: 'from-orange-500 to-red-400', date: '4/6/26, 11:20' },
      { id: 'Audiobook', title: 'Creating your first Audiobook', color: 'from-green-500 to-teal-400', date: '4/7/26, 09:15' },
      { id: 'Voice Cloning', title: 'Voice Cloning Tutorial', color: 'from-indigo-500 to-blue-400', date: '4/8/26, 14:00' },
      { id: 'Studio', title: 'How to use Studio', color: 'from-pink-500 to-rose-400', date: '4/9/26, 16:45' }
    ],
    'Uzbek': [
      { id: 'Text to Speech', title: 'Matnni Ovozga aylantirish', color: 'from-blue-500 to-cyan-400', date: '4/4/26, 10:00' },
      { id: 'Speech to Text', title: 'Ovozni Matnga aylantirish', color: 'from-purple-500 to-pink-400', date: '4/6/26, 15:30' },
      { id: 'Dubbing AI', title: 'Dublyaj AI qoʻllanmasi', color: 'from-orange-500 to-red-400', date: '4/6/26, 11:20' },
      { id: 'Audiobook', title: 'Birinchi Audiokitobni yaratish', color: 'from-green-500 to-teal-400', date: '4/7/26, 09:15' },
      { id: 'Voice Cloning', title: 'Ovoz Klonlash darsligi', color: 'from-indigo-500 to-blue-400', date: '4/8/26, 14:00' },
      { id: 'Studio', title: 'Studiyadan foydalanish', color: 'from-pink-500 to-rose-400', date: '4/9/26, 16:45' }
    ],
    'Russian': [
      { id: 'Text to Speech', title: 'Как использовать Текст в речь', color: 'from-blue-500 to-cyan-400', date: '4/4/26, 10:00' },
      { id: 'Speech to Text', title: 'Как использовать Речь в текст', color: 'from-purple-500 to-pink-400', date: '4/6/26, 15:30' },
      { id: 'Dubbing AI', title: 'Руководство по Дубляж AI', color: 'from-orange-500 to-red-400', date: '4/6/26, 11:20' },
      { id: 'Audiobook', title: 'Создание первой Аудиокниги', color: 'from-green-500 to-teal-400', date: '4/7/26, 09:15' },
      { id: 'Voice Cloning', title: 'Урок по Клонированию голоса', color: 'from-indigo-500 to-blue-400', date: '4/8/26, 14:00' },
      { id: 'Studio', title: 'Как использовать Студию', color: 'from-pink-500 to-rose-400', date: '4/9/26, 16:45' }
    ]
  };

  const currentHomeVideos = localizedVideoGuides[appLang] || localizedVideoGuides['English'];



const [downloadingId, setDownloadingId] = useState(null);

 const handleSecureDownload = async (url, filename, id) => {
    try {
      setDownloadingId(id);
      
      // 1. Eng aniq usul: URL dan faqat manzil (path) qismini ajratib olib, /proxy ga ulaymiz
      let proxyUrl = url;
      if (url.startsWith('http')) {
          const urlObj = new URL(url);
          proxyUrl = `/proxy${urlObj.pathname}${urlObj.search}`;
      } else {
          proxyUrl = `/proxy${url.startsWith('/') ? '' : '/'}${url}`;
      }

      // 2. Faylni Vercel proxy orqali so'raymiz
      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers: {
          "ngrok-skip-browser-warning": "true"
        }
      });

      if (!response.ok) {
        throw new Error(`Proxy server faylni topa olmadi (Status: ${response.status})`);
      }

      const blob = await response.blob();

      // ⚠️ Xavfsizlik tekshiruvi: Agar proxy adashib React saytingizni (HTML) qaytarsa, uni to'xtatamiz
      if (blob.type.includes('text/html')) {
        throw new Error("Proxy fayl o'rniga HTML qaytardi. vercel.json to'g'ri ishlamayapti.");
      }

      // 3. Faylni kompyuterga yashirincha yuklaymiz
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'dubbig_file';
      document.body.appendChild(link);
      link.click();
      
      // Tozalash
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

    } catch (err) {
      console.warn("Proxy orqali yuklash muvaffaqiyatsiz bo'ldi, zaxira usuliga o'tilmoqda:", err);
      // Agar Vercel proxy xato bersa yoki localhost'da bo'lsangiz, foydalanuvchiga xato bermasdan eski usulda faylni ochib beradi
      window.open(url, "_blank");
    } finally {
      setDownloadingId(null);
    }
  };



  const handleMenuClick = (id) => {
    setActiveTab(id); setIsMobileMenuOpen(false); setIsProfileOpen(false);
  };

  const handleToggleSaveVoice = (voice) => {
    if (savedVoices.find(v => v.id === voice.id)) {
      setSavedVoices(savedVoices.filter(v => v.id !== voice.id));
    } else {
      setSavedVoices([...savedVoices, voice]);
    }
  };

  const handleDeleteFile = (e, tab, id) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, type: 'file', payload: { tab, id } });
  };

  const handleRemoveSavedVoice = (voice) => {
    setDeleteModal({ isOpen: true, type: 'savedVoice', payload: voice });
  };

  const handleDeleteMyVoice = (e, voice) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, type: 'myVoice', payload: voice });
  };

  const confirmDelete = () => {
    if (deleteModal.type === 'file') {
      const { tab, id } = deleteModal.payload;
      const itemToDelete = myFiles[tab].find(item => item.id === id);
      setDeletedFiles([...deletedFiles, { ...itemToDelete, originalTab: tab }]);
      setMyFiles({ ...myFiles, [tab]: myFiles[tab].filter(item => item.id !== id) });
    } else if (deleteModal.type === 'savedVoice') {
      handleToggleSaveVoice(deleteModal.payload);
    } else if (deleteModal.type === 'myVoice') {
      const voice = deleteModal.payload;
      setDeletedVoices([...deletedVoices, voice]);
      setMyVoices(myVoices.filter(v => v.id !== voice.id));
      setSavedVoices(savedVoices.filter(v => v.id !== voice.id)); 
    }
    setDeleteModal({ isOpen: false, type: null, payload: null });
  };

  const renderRealGeneratingUI = (jobState, resetJob, fileTab) => {
    if (!jobState) return null;
    return (
      <div className="w-full flex items-center justify-center py-20">
          <div className={`w-full max-w-lg rounded-3xl p-10 border text-center shadow-lg ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
            {jobState.status === 'loading' ? (
                 <>
                     <h3 className="text-2xl font-bold mb-8">Generating...</h3>
                     <div className="relative w-24 h-24 mx-auto mb-8">
                         <svg className="w-full h-full" viewBox="0 0 100 100">
                             <circle className="text-gray-200 dark:text-gray-700 stroke-current" strokeWidth="8" cx="50" cy="50" r="40" fill="transparent"></circle>
                             <circle className="text-blue-500 stroke-current transition-all duration-200" strokeWidth="8" strokeLinecap="round" cx="50" cy="50" r="40" fill="transparent" strokeDasharray="251.2" strokeDashoffset={251.2 - (251.2 * (jobState.progress || 0)) / 100}></circle>
                         </svg>
                     </div>
                     <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                         <div className="bg-blue-600 h-2 rounded-full transition-all duration-200" style={{ width: `${jobState.progress || 0}%` }}></div>
                     </div>
                     <div className="flex justify-between text-sm font-bold text-gray-500">
                         <span>{t('File generating...')}</span>
                         <span>{Math.round(jobState.progress || 0)}%</span>
                     </div>
                 </>
             ) : jobState.status === 'completed' ? (
                 <>
                     <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                         <CheckCircle2 size={40} />
                     </div>
                     <h3 className="text-2xl font-bold mb-6">{t('Completed!')}</h3>
                     <button onClick={() => {
                         if(fileTab === 'Voices') {
                             setActiveTab('Voices'); setVoicesTab('My Voices');
                         } else {
                             setActiveTab('Files'); setFilesTab(fileTab);
                         }
                         resetJob();
                     }} className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition w-full mb-4 shadow-md">
                         {t('Go to ')} {fileTab === 'Voices' ? t('Voices') : t('Files')}
                     </button>
                     <button onClick={resetJob} className="text-sm font-bold text-gray-500 hover:text-gray-800 dark:hover:text-gray-300">{t('Create another')}</button>
                 </>
             ) : (
                <>
                     <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                         X
                     </div>
                     <h3 className="text-2xl font-bold mb-2">Failed</h3>
                     <p className="text-gray-500 mb-6">{jobState.message || "Something went wrong."}</p>
                     <button onClick={resetJob} className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-xl font-bold hover:bg-gray-300 transition w-full mb-4 shadow-md">
                         Try Again
                     </button>
                 </>
             )}
          </div>
      </div>
    );
  }
    


  const filteredLogs = requestsLog.filter(log => usageFilter === 'All' || log.type === usageFilter);
  const maxUsagePage = Math.max(1, Math.ceil(filteredLogs.length / 20));
  const currentLogs = filteredLogs.slice(usagePage * 20, (usagePage + 1) * 20);
  const maxDeletedFilesPage = Math.max(1, Math.ceil(deletedFiles.length / 20));
  const currentDeletedFiles = deletedFiles.slice(deletedFilesPage * 20, (deletedFilesPage + 1) * 20);
  const maxDeletedVoicesPage = Math.max(1, Math.ceil(deletedVoices.length / 20));
  const currentDeletedVoices = deletedVoices.slice(deletedVoicesPage * 20, (deletedVoicesPage + 1) * 20);

  return (
    <div className={`min-h-screen flex ${isDarkMode ? 'bg-[#0f172a] text-white' : 'bg-gray-50 text-gray-900'}`}>
      
      {isMobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} />}

      {/* LOGOUT CONFIRMATION MODAL */}
      {logoutModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setLogoutModal(false)}>
          <div className={`w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl p-6 text-center ${isDarkMode ? 'bg-[#1e293b] border border-gray-700' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-6">{t('Are you sure you want to sign out?')}</h3>
            <div className="flex justify-center gap-4">
              <button onClick={() => setLogoutModal(false)} className={`px-6 py-2.5 rounded-lg font-bold transition-colors ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}>
                {t('No')}
              </button>
              <button onClick={confirmSignOut} className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-colors shadow-md">
                {t('Yes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NAME CHANGE MODAL */}
      {nameModal.isOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setNameModal({ isOpen: false, value: '' })}>
          <div className={`w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl p-6 text-center ${isDarkMode ? 'bg-[#1e293b] border border-gray-700' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">{t('Update Given Name')}</h3>
            <input
              type="text"
              maxLength={15}
              value={nameModal.value}
              onChange={(e) => setNameModal({ ...nameModal, value: e.target.value })}
              className={`w-full p-3 rounded-xl border outline-none font-medium mb-6 text-center ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`}
              placeholder={t('Given Name')}
            />
            <div className="flex justify-center gap-4">
              <button onClick={() => setNameModal({ isOpen: false, value: '' })} className={`px-6 py-2.5 rounded-lg font-bold transition-colors ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}>
                {t('Back')}
              </button>
              <button onClick={() => {
                  if(nameModal.value.trim()){
                      setUserName(nameModal.value.trim());
                      setNameModal({ isOpen: false, value: '' });
                  }
              }} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-md">
                {t('Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setDeleteModal({ isOpen: false, type: null, payload: null })}>
          <div className={`w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl p-6 text-center ${isDarkMode ? 'bg-[#1e293b] border border-gray-700' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-6">{t('Are you sure you want to delete this?')}</h3>
            <div className="flex justify-center gap-4">
              <button onClick={() => setDeleteModal({ isOpen: false, type: null, payload: null })} className={`px-6 py-2.5 rounded-lg font-bold transition-colors ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}>
                {t('No')}
              </button>
              <button onClick={confirmDelete} className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-colors shadow-md">
                {t('Yes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GLOBAL MEDIA PLAYER MODAL */}
      {mediaModal.isOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setMediaModal({ isOpen: false, type: 'video', src: '', title: '' })}>
          <div className={`w-full ${mediaModal.type === 'video' ? 'max-w-4xl' : 'max-w-md'} rounded-2xl overflow-hidden shadow-2xl ${isDarkMode ? 'bg-[#1e293b] border border-gray-700' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className={`relative flex items-center justify-center overflow-hidden ${isDarkMode ? 'bg-black' : 'bg-gray-100'} ${mediaModal.type === 'video' ? 'aspect-video' : 'p-8'}`}>
              {mediaModal.type === 'video' ? (
                  <video controls autoPlay className="w-full h-full outline-none bg-black">
                    <source src={mediaModal.src} type="video/mp4" />
                  </video>
              ) : (
                  <div className="w-full flex flex-col items-center">
                      <div className="w-24 h-24 bg-blue-500 rounded-full flex items-center justify-center mb-6 shadow-lg text-white animate-pulse">
                          <Headphones size={40} />
                      </div>
                      <audio controls autoPlay className="w-full outline-none">
                        <source src={mediaModal.src} type="audio/mpeg" />
                      </audio>
                  </div>
              )}
            </div>
            <div className="p-6 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold mb-1">{mediaModal.title}</h3>
                <p className="text-sm text-gray-500">{t('Playing...')}</p>
              </div>
              <button onClick={() => setMediaModal({ isOpen: false, type: 'video', src: '', title: '' })} className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors shadow-sm">
                {t('Back')}
              </button>
            </div>
          </div>
        </div>
      )}

      <aside className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static flex flex-col border-r ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} ${isDarkMode ? 'border-gray-700 bg-[#1e293b]' : 'border-gray-200 bg-white'}`}>
        <div className="h-16 flex items-center justify-between px-6 border-b dark:border-gray-700 lg:border-none lg:h-auto lg:p-6 lg:pb-2">
          <div className="flex items-center gap-2 cursor-pointer" onClick={onBackToLanding}>
            <img src={MainLogo} alt="Dubbig Logo" className="h-12 md:h-16 w-auto object-contain" />
          </div>
          <button className="lg:hidden p-2 -mr-2" onClick={() => setIsMobileMenuOpen(false)}><X size={24} /></button>
        </div>
        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto flex flex-col">
          <div className="space-y-1 mb-auto">
            {mainMenus.map((item) => (
              <button key={item.id} onClick={() => handleMenuClick(item.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === item.id ? 'bg-blue-600 text-white font-medium shadow-sm' : (isDarkMode ? 'text-gray-300 hover:bg-gray-800 hover:text-white' : 'text-gray-600 hover:bg-gray-100')}`}>
                {item.icon} <span>{item.name}</span>
              </button>
            ))}
          </div>
          <div className="pt-4 border-t dark:border-gray-700 mt-4 space-y-1">
            {bottomMenus.map((item) => (
              <button key={item.id} onClick={() => handleMenuClick(item.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === item.id ? 'bg-blue-600 text-white font-medium shadow-sm' : (isDarkMode ? 'text-gray-300 hover:bg-gray-800 hover:text-white' : 'text-gray-600 hover:bg-gray-100')}`}>
                {item.icon} <span>{item.name}</span>
              </button>
            ))}
          </div>
        </nav>
      </aside>

      <main className="flex-1 flex flex-col h-screen min-w-0 overflow-hidden relative">
        <header className={`h-16 shrink-0 border-b flex items-center justify-between px-4 lg:px-8 ${isDarkMode ? 'border-gray-700 bg-[#1e293b]' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => setIsMobileMenuOpen(true)}><Menu size={24} /></button>
            <h2 className="text-xl font-semibold hidden sm:block">{t(activeTab)}</h2>
          </div>
          
          <div className="flex items-center gap-4 lg:gap-6">
            <button onClick={() => { setAiAssistantHistory(prev => [...prev, activeTab]); setActiveTab('AI Assistant'); }} className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm transition-colors ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-100 hover:bg-blue-200 text-blue-700'}`}>
              <Sparkles size={16} /> {t('AI Assistant')}
            </button>

            <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            <div className="relative">
              <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-gradient-to-tr from-cyan-400 to-blue-500 flex items-center justify-center text-white shadow-sm hover:opacity-90 transition">
                <User size={20} />
              </button>
              {isProfileOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsProfileOpen(false)}></div>
                  <div className={`absolute right-0 mt-3 w-64 rounded-xl shadow-xl border overflow-hidden z-50 ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="p-4 border-b dark:border-gray-700">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('Balance')}</p>
                      <div className="flex justify-between items-baseline">
                        <h3 className="text-2xl font-bold">{minutes}</h3><span className="text-sm font-medium">{t('minutes')}</span>
                      </div>
                      <div className="flex justify-between items-baseline mt-1">
                        <h3 className="text-xl font-bold text-blue-500">{credits.toLocaleString()}</h3><span className="text-xs font-medium text-gray-500">credits</span>
                      </div>
                    </div>
                    <div className="p-2">
                      <button onClick={() => { handleMenuClick('Settings'); setSettingsView('main'); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition"><Settings size={18} /> {t('Settings')}</button>
                      <button onClick={() => handleMenuClick('Usage Analyze')} className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition"><BarChart2 size={18} /> {t('Usage Analyze')}</button>
                      <button onClick={() => handleMenuClick('Subscription')} className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition"><CreditCard size={18} /> {t('Subscription')}</button>
                    </div>
                    <div className="p-2 border-t dark:border-gray-700">
                      <button onClick={() => { setIsProfileOpen(false); setLogoutModal(true); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-500 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition"><LogOut size={18} /> {t('Sign out')}</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-4 lg:p-8 relative" onClick={() => { setIsTtsVoiceOpen(false); setIsDubbingLangOpen(false); setIsCloneLangOpen(false); setIsSttLangOpen(false); setIsAudiobookVoiceOpen(false); setIsAudiobookLangOpen(false); setIsAppLangOpen(false); }}>
          
          {/* HOME TAB */}
          {activeTab === 'Home' && (
  <div className="mx-auto max-w-6xl pb-10">
    <h2 className="mb-2 text-3xl font-bold">
      {t('Hello')}, {userName || 'User'}! 👋
    </h2>

    <p className="mb-8 text-gray-500 dark:text-gray-400">
      {t('Welcome to Dubbig. Learn how to use our tools with the video guides below.')}
    </p>

    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {currentHomeVideos?.map((video, idx) => (
        <div
          key={video.id || idx}
          onClick={() => setActiveVideoModal(video)}
          className={`group relative aspect-video cursor-pointer overflow-hidden rounded-2xl border ${
            isDarkMode
              ? 'border-gray-700 bg-gray-800'
              : 'border-gray-200 bg-gray-100'
          }`}
        >
          <div className={`absolute inset-0 bg-gradient-to-br opacity-20 ${video.color}`}></div>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 shadow-lg backdrop-blur-sm transition-transform group-hover:scale-110">
              <Play
                size={32}
                className={isDarkMode ? 'text-white' : 'text-gray-900'}
                fill="currentColor"
              />
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <h3 className="text-lg font-medium text-white">{video.title}</h3>
          </div>
        </div>
      ))}
    </div>
  </div>
)}

          {/* HOME VIDEO MODAL */}
          {activeVideoModal && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveVideoModal(null)}>
              <div className={`w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl ${isDarkMode ? 'bg-[#1e293b] border border-gray-700' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
                <div className="aspect-video bg-black relative flex items-center justify-center overflow-hidden">
                  <video controls autoPlay className="w-full h-full outline-none bg-black">
                    <source src={LOCAL_VIDEO_PATH} type="video/mp4" />
                  </video>
                </div>
                <div className="p-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div>
                    <h3 className="text-xl font-bold mb-1">{activeVideoModal.title}</h3>
                    <p className="text-sm text-gray-500">{activeVideoModal.date}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setActiveVideoModal(null)} className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                      {t('Back')}
                    </button>
                    <button onClick={() => { setActiveTab(activeVideoModal.id); setActiveVideoModal(null); }} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 shadow-md">
                      {t('Start creating')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VOICES TAB */}
          {activeTab === 'Voices' && (
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-2 mb-8 border-b dark:border-gray-700 pb-4">
                <button onClick={() => setVoicesTab('Explore')} className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${voicesTab === 'Explore' ? (isDarkMode ? 'bg-gray-800 text-white' : 'bg-gray-200 text-black') : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}>
                  {t('Explore')}
                </button>
                <button onClick={() => setVoicesTab('My Voices')} className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${voicesTab === 'My Voices' ? (isDarkMode ? 'bg-gray-800 text-white' : 'bg-gray-200 text-black') : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}>
                  {t('My Voices')}
                </button>
              </div>

              {voicesTab === 'Explore' && (
                <>
                  <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h2 className="text-2xl font-bold">{t('Trending voices')}</h2>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
                      <Search size={18} className="text-gray-400"/>
                      <input type="text" placeholder="Search voices..." className="bg-transparent outline-none text-sm w-48" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dummyVoices.slice(0, 3).map((voice) => {
                      const isSaved = savedVoices.some(v => v.id === voice.id);
                      return (
                      <div key={voice.id} className={`p-4 rounded-2xl border flex items-center justify-between group hover:shadow-md transition-shadow ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-100'}`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full bg-gradient-to-tr ${voice.color} flex items-center justify-center text-white font-bold shadow-inner relative`}>
                            {voice.name.charAt(0)}
                            <div className="absolute -bottom-1 -right-1 bg-blue-600 rounded-full p-0.5 border-2 border-white dark:border-[#1e293b]"><CheckCircle2 size={10} className="text-white"/></div>
                          </div>
                          <div>
                            <h4 className="font-bold text-sm mb-0.5">{voice.name}</h4>
                            <p className="text-xs font-medium text-gray-500">{voice.desc}</p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={playLocalAudio} className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-full transition-colors"><PlayCircle size={20} /></button>
                          <button onClick={() => handleToggleSaveVoice(voice)} className={`p-2 rounded-full transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/30 ${isSaved ? 'text-blue-500' : 'text-gray-400'}`}>
                            <Bookmark size={18} fill={isSaved ? "currentColor" : "none"} />
                          </button>
                        </div>
                      </div>
                    )})}
                  </div>

                </>
              )}

              {voicesTab === 'My Voices' && (
                <div>
                  {myVoices.length === 0 ? (
                    <div className="text-center py-20 text-gray-500">{t("You haven't saved any voices yet. Go to Explore to save some.")}</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {myVoices.map((voice) => {
                        const isSaved = savedVoices.some(v => v.id === voice.id);
                        return (
                        <div key={voice.id} className={`p-4 rounded-2xl border flex items-center justify-between group hover:shadow-md transition-shadow ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-100'}`}>
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-full bg-gradient-to-tr ${voice.color} flex items-center justify-center text-white font-bold shadow-inner relative`}>
                              {voice.name.charAt(0)}
                            </div>
                            <div>
                              <h4 className="font-bold text-sm mb-0.5">{voice.name}</h4>
                              <p className="text-xs text-gray-500 mb-2">{t('Voice Clone')}</p>
                              <button onClick={(e) => handleDeleteMyVoice(e, voice)} className="text-xs font-bold text-red-500 hover:text-red-600 transition-colors">
                                {t('Remove')}
                              </button>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={playLocalAudio} className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-full transition-colors"><PlayCircle size={20} /></button>
                            <button onClick={() => handleToggleSaveVoice(voice)} className={`p-2 rounded-full transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/30 ${isSaved ? 'text-blue-500' : 'text-gray-400'}`}>
                              <Bookmark size={18} fill={isSaved ? "currentColor" : "none"} />
                            </button>
                          </div>
                        </div>
                      )})}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* SAVED TAB */}
          {activeTab === 'Saved' && (
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3"><Bookmark className="text-blue-500"/> {t('Saved')}</h2>
              {savedVoices.length === 0 ? (
                <div className="text-center py-20 text-gray-500">{t("You haven't saved any voices yet. Go to Explore to save some.")}</div>
              ) : (
                <div className="space-y-3">
                  {savedVoices.map(voice => (
                    <div key={voice.id} className={`p-4 rounded-xl border flex items-center justify-between ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full bg-gradient-to-tr ${voice.color} flex items-center justify-center text-white font-bold`}>{voice.name.charAt(0)}</div>
                        <div><h4 className="font-bold text-sm">{voice.name}</h4><p className="text-xs text-gray-500">{voice.desc === 'Voice Clone' ? t('Voice Clone') : t('Library Voice')}</p></div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={playLocalAudio} className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors rounded-full"><PlayCircle size={20}/></button>
                        <button onClick={() => handleRemoveSavedVoice(voice)} className="px-3 py-1.5 text-sm font-medium text-red-500 bg-red-50 dark:bg-red-900/20 transition-colors rounded-lg flex items-center gap-2 hover:opacity-80">
                          <Trash2 size={16}/> {t('Remove')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* FILES TAB */}
          {activeTab === 'Files' && (
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold mb-6">{t('Files')}</h2>
              
              <div className="flex items-center gap-2 mb-8 border-b dark:border-gray-700 pb-4 overflow-x-auto">
                {['Dubs', 'TTS', 'Audiobooks', 'STT', 'Studio'].map(tab => (
                  <button key={tab} onClick={() => setFilesTab(tab)} className={`px-4 py-2 rounded-full text-sm font-bold transition-colors whitespace-nowrap ${filesTab === tab ? (isDarkMode ? 'bg-gray-800 text-white' : 'bg-gray-200 text-black') : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}>
                    {t(tab)}
                  </button>
                ))}
              </div>

              {myFiles[filesTab]?.length === 0 || !myFiles[filesTab] ? (
                <div className="text-center py-20 text-gray-500">{t('No files found.')}</div>
              ) : (
                <div className="space-y-3">
                  {myFiles[filesTab].map(item => (
                    <div key={item.id} className={`p-4 rounded-xl border flex items-center justify-between ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
                      <div>
                        <h4 className="font-bold text-lg mb-1">{item.name}</h4>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{item.date}</span> • <span>Source: {item.type}</span>
                          <span className={`rounded-full px-2 py-0.5 ml-2 ${item.status === 'done' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{item.status || 'done'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.download ? (
                            <button 
                                onClick={() => handleSecureDownload(item.download, item.name, item.id)} 
                                disabled={downloadingId === item.id}
                                className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-full transition-colors disabled:opacity-50"
                                title="Yuklab olish"
                            >
                                {downloadingId === item.id ? <Loader2 size={20} className="animate-spin" /> : <Download size={20}/>}
                            </button>
                        ) : (
                            filesTab !== 'STT' && (
                                <button onClick={() => setMediaModal({ isOpen: true, type: filesTab === 'Dubs' ? 'video' : 'audio', src: filesTab === 'Dubs' ? LOCAL_VIDEO_PATH : LOCAL_AUDIO_PATH, title: item.name })} className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-full transition-colors"><PlayCircle size={20}/></button>
                            )
                        )}
                        <button onClick={(e) => handleDeleteFile(e, filesTab, item.id)} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-full transition-colors"><Trash2 size={20}/></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* DUBBING AI TAB */}
          {activeTab === 'Dubbing AI' && (
            dubState && dubState.status === "loading" ? renderRealGeneratingUI(dubState, () => { setDubState(null); setDubJobId(null); }, 'Dubs') :
            <div className={`max-w-2xl mx-auto rounded-3xl p-6 lg:p-10 border ${isDarkMode ? 'bg-[#1e293b] border-gray-700 shadow-xl' : 'bg-white border-gray-100 shadow-xl'}`}>
              <h3 className="text-3xl font-bold mb-8">{t('Create a dub')}</h3>
              <div className="space-y-6">
                
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('Project Name')}</label>
                  <input type="text" value={dubName} onChange={e => setDubName(e.target.value)} placeholder="My Awesome Dub" className={`w-full p-3.5 rounded-xl border outline-none font-medium ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`} />
                </div>

                <div className="relative">
                  <label className="block text-sm font-semibold mb-2">{t('Target Language')}</label>
                  <div onClick={(e) => { e.stopPropagation(); setIsDubbingLangOpen(!isDubbingLangOpen); }} className={`w-full p-3.5 rounded-xl border flex justify-between items-center cursor-pointer ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                    <span className={dubbingLang === 'Select language' ? 'text-gray-500' : ''}>{dubbingLang}</span>
                    <ChevronDown size={18} className={`transition-transform ${isDubbingLangOpen ? 'rotate-180' : ''}`} />
                  </div>
                  {isDubbingLangOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsDubbingLangOpen(false)}></div>
                      <div className={`absolute z-20 w-full mt-2 py-2 rounded-xl shadow-2xl border max-h-60 overflow-y-auto ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-white border-gray-200'}`}>
                        {apiLanguages.map(lang => (
                          <div key={lang} onClick={(e) => { e.stopPropagation(); setDubbingLang(lang); setIsDubbingLangOpen(false); }} className={`px-4 py-3 cursor-pointer hover:bg-blue-600 hover:text-white transition-colors ${dubbingLang === lang ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20' : ''}`}>
                            {lang}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center justify-between py-2 border-y dark:border-gray-700">
                  <div>
                    <p className="font-semibold text-sm mb-0.5">{t('Voice Cloning')}</p>
                    <p className="text-xs text-gray-500">{t('Preserve the original speaker\'s voice')}</p>
                  </div>
                  <button onClick={() => setDubVoiceCloning(!dubVoiceCloning)} className={`w-12 h-6 rounded-full p-1 transition-colors ${dubVoiceCloning ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
                    <div className={`bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${dubVoiceCloning ? 'translate-x-6' : 'translate-x-0'}`}></div>
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">{t('Source Link (Optional)')}</label>
                  <div className={`flex items-center gap-3 w-full px-4 rounded-xl border ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                    <LinkIcon size={18} className="text-gray-400" />
                    <input type="text" value={dubLink} onChange={e => setDubLink(e.target.value)} placeholder={t('Paste YouTube URL')} className="flex-1 py-3.5 bg-transparent outline-none text-sm font-medium" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">{t('Or Upload Audio / Video')}</label>
                  <label htmlFor="dub-upload" className={`block border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-all ${uploadedFile ? (isDarkMode ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50') : (isDarkMode ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50')}`}>
                    <input id="dub-upload" type="file" accept="audio/*,video/*" className="hidden" onChange={(e) => setUploadedFile(e.target.files[0])} />
                    {uploadedFile ? (
                      <><div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4 text-white"><Play size={28} fill="currentColor" /></div><p className="font-bold text-lg text-blue-600 dark:text-blue-400">{uploadedFile.name}</p></>
                    ) : (
                      <><div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4"><Upload size={28} className="text-gray-500" /></div><p className="font-bold text-lg">{t('Click or drag to upload')}</p><p className="text-sm text-gray-500 mt-2">Max 2000MB</p></>
                    )}
                  </label>
                </div>

                <button onClick={handleCreateDub} disabled={!dubName || dubbingLang === 'Select language' || (!uploadedFile && !dubLink.trim())} className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${(dubName && dubbingLang !== 'Select language' && (uploadedFile || dubLink.trim())) ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'}`}>
                  {t('Create a dub')}
                </button>
              </div>
            </div>
          )}

          {/* TEXT TO SPEECH TAB */}
          {activeTab === 'Text to Speech' && (
            <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 h-full pb-10">
              <div className={`flex-1 rounded-2xl border flex flex-col ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="p-4 border-b dark:border-gray-700">
                  <input type="text" value={ttsTitle} onChange={e => setTtsTitle(e.target.value)} placeholder={`${t('Project Name')} (min. 3 characters)`} className={`w-full bg-transparent outline-none font-bold text-lg ${isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`} />
                </div>
                <textarea value={ttsText} onChange={(e) => setTtsText(e.target.value)} className={`flex-1 w-full p-6 resize-none outline-none rounded-b-2xl bg-transparent ${isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`} placeholder="Start typing here..."></textarea>
                <div className="p-4 border-t dark:border-gray-700 flex justify-between items-center bg-black/5 dark:bg-white/5 rounded-b-2xl">
                  <span className="text-xs text-gray-500 font-medium">{ttsText.length} / 2500 characters</span>
                  <button onClick={handleGenerateTTS} disabled={!ttsText.trim() || ttsTitle.trim().length < 3 || ttsLoading} className={`px-8 py-2.5 rounded-full font-bold transition-colors ${ttsText.trim() && ttsTitle.trim().length >= 3 && !ttsLoading ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400'}`}>
                    {ttsLoading ? <Loader2 className="inline h-4 w-4 animate-spin" /> : t('Generate Speech')}
                  </button>
                </div>
              </div>
              <div className={`w-full lg:w-80 rounded-2xl border p-6 space-y-6 ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
                <div><label className="text-sm font-bold mb-2 block">{t('Model')}</label><div className={`w-full p-3 rounded-xl border text-sm font-semibold flex items-center gap-2 ${isDarkMode ? 'bg-indigo-900/30 border-indigo-500/50 text-indigo-300' : 'bg-indigo-50 border-indigo-200 text-indigo-700'}`}><Layout size={16}/> DubbigAi</div></div>
                <div className="relative">
                  <label className="text-sm font-bold mb-2 block flex justify-between">{t('Select Speaker')} <span onClick={(e) => { e.stopPropagation(); setActiveTab('Voices'); setVoicesTab('Explore'); }} className="text-blue-500 font-normal cursor-pointer text-xs hover:underline">{t('Explore')}</span></label>
                  <div onClick={(e) => { e.stopPropagation(); setIsTtsVoiceOpen(!isTtsVoiceOpen); }} className={`w-full p-3 rounded-xl border flex justify-between items-center cursor-pointer ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`}><span className="text-sm font-medium">{ttsSelectedVoice}</span><ChevronDown size={16} className={`transition-transform ${isTtsVoiceOpen ? 'rotate-180' : ''}`} /></div>
                  {isTtsVoiceOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsTtsVoiceOpen(false)}></div>
                      <div className={`absolute z-20 w-full mt-2 py-2 rounded-xl shadow-2xl border max-h-72 overflow-y-auto ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-white border-gray-200'}`}>
                        {uniqueTTSVoices.map(voice => {
                          const isSaved = savedVoices.some(v => v.id === voice.id);
                          return (
                          <div key={voice.id} onClick={(e) => { e.stopPropagation(); setTtsSelectedVoice(voice.name); setIsTtsVoiceOpen(false); }} className={`px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-between group ${ttsSelectedVoice === voice.name ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                            <div className="flex items-center gap-2"><p className="font-bold text-sm mb-0.5">{voice.name}</p>{isSaved && <Bookmark size={12} className="text-blue-500" fill="currentColor"/>}</div>
                          </div>
                        )})}
                      </div>
                    </>
                  )}
                </div>
                <div className="relative">
                  <label className="text-sm font-bold mb-2 block">{t('Language')}</label>
                  <div onClick={(e) => { e.stopPropagation(); setIsTtsLangOpen(!isTtsLangOpen); }} className={`w-full p-3 rounded-xl border flex justify-between items-center cursor-pointer ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`}><span className="text-sm font-medium">{ttsLang}</span><ChevronDown size={16} className={`transition-transform ${isTtsLangOpen ? 'rotate-180' : ''}`} /></div>
                  {isTtsLangOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsTtsLangOpen(false)}></div>
                      <div className={`absolute z-20 w-full mt-2 py-2 rounded-xl shadow-2xl border max-h-72 overflow-y-auto ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-white border-gray-200'}`}>
                        {apiLanguages.map(lang => (
                          <div key={lang} onClick={(e) => { e.stopPropagation(); setTtsLang(lang); setIsTtsLangOpen(false); }} className={`px-4 py-3 cursor-pointer hover:bg-blue-600 hover:text-white transition-colors ${ttsLang === lang ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20' : ''}`}>{lang}</div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <label className="text-sm font-bold mb-3 block">{t('Speed')}</label>
                  <div className="flex items-center gap-2">
                    {['0.8x', '1.0x', '1.2x', '1.5x'].map(speed => (
                      <button key={speed} onClick={() => setTtsSpeed(speed)} className={`flex-1 py-2 text-sm font-bold rounded-lg border transition-colors ${ttsSpeed === speed ? 'bg-[#111827] text-white dark:bg-white dark:text-black border-transparent' : 'bg-transparent text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>{speed}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-bold mb-3 block">Energy</label>
                  <input type="range" min="0.5" max="1.8" step="0.01" value={ttsEnergy} onChange={(e) => setTtsEnergy(parseFloat(e.target.value))} className="w-full" />
                </div>
              </div>
            </div>
          )}

          {/* VOICE CLONING TAB */}
          {activeTab === 'Voice Cloning' && (
            !isBetaAvailable ? (
              <div className={`max-w-2xl mx-auto rounded-3xl p-10 border shadow-xl text-center ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
                <h3 className="text-2xl font-bold mb-4">🚧 Voice Cloning</h3>
                <p className="text-gray-500 text-lg">
                  Ushbu bo‘lim hozircha <strong>beta versiyada mavjud emas</strong>.
                </p>
                <p className="text-gray-400 mt-2">
                  Iltimos, keyinroq qayta urinib ko‘ring.
                </p>
              </div>
            ) : cloneState && cloneState.status === "loading" ? renderRealGeneratingUI(cloneState, () => { setCloneState(null); setCloneJobId(null); }, 'Voices') :
            <div className={`max-w-2xl mx-auto rounded-3xl p-6 lg:p-10 border ${isDarkMode ? 'bg-[#1e293b] border-gray-700 shadow-xl' : 'bg-white border-gray-100 shadow-xl'}`}>
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl text-blue-600 dark:text-blue-400"><UserPlus size={28}/></div>
                <div><h3 className="text-2xl font-bold">{t('Voice Cloning')}</h3><p className="text-sm text-gray-500">Create a digital clone of your voice.</p></div>
              </div>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('Voice Name')}</label>
                  <input type="text" value={cloneName} onChange={e => setCloneName(e.target.value)} placeholder={t('e.g. My Custom Voice')} className={`w-full p-4 rounded-xl border outline-none font-medium ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`} />
                </div>
                <div className="relative">
                  <label className="block text-sm font-bold mb-2 block">{t('Native Language')}</label>
                  <div onClick={(e) => { e.stopPropagation(); setIsCloneLangOpen(!isCloneLangOpen); }} className={`w-full p-4 rounded-xl border flex justify-between items-center cursor-pointer ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`}><span className="text-sm font-medium">{cloneLang}</span><ChevronDown size={16} className={`transition-transform ${isCloneLangOpen ? 'rotate-180' : ''}`} /></div>
                  {isCloneLangOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsCloneLangOpen(false)}></div>
                      <div className={`absolute z-20 w-full mt-2 py-2 rounded-xl shadow-2xl border max-h-72 overflow-y-auto ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-white border-gray-200'}`}>
                        {apiLanguages.map(lang => (
                          <div key={lang} onClick={(e) => { e.stopPropagation(); setCloneLang(lang); setIsCloneLangOpen(false); }} className={`px-4 py-3 cursor-pointer hover:bg-blue-600 hover:text-white transition-colors ${cloneLang === lang ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20' : ''}`}>{lang}</div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('Or Upload Audio / Video')}</label>
                  <label htmlFor="clone-upload" className={`block border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-all ${cloningFile ? (isDarkMode ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50') : (isDarkMode ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50')}`}>
                    <input id="clone-upload" type="file" accept="audio/*,video/*" className="hidden" onChange={(e) => setCloningFile(e.target.files[0])} />
                    {cloningFile ? (
                      <><div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mb-3"><Volume2 size={28} className="text-green-600 dark:text-green-400" /></div><p className="font-bold text-green-600 dark:text-green-400">{cloningFile.name}</p></>
                    ) : (
                      <><Upload size={32} className="mb-3 text-gray-400" /><p className="font-bold">{t('Click or drag to upload')}</p><p className="text-sm text-gray-500 mt-1">Audio or Video format.</p></>
                    )}
                  </label>
                </div>
                <button onClick={handleCreateClone} disabled={!cloningFile || !cloneName} className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${cloningFile && cloneName ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'}`}>{t('Start Cloning Process')}</button>
              </div>
            </div>
          )}

          {/* SPEECH TO TEXT TAB */}
          {activeTab === 'Speech to Text' && (
            sttState && sttState.status === "loading" ? renderRealGeneratingUI(sttState, () => { setSttState(null); setSttJobId(null); }, 'STT') :
            sttState && sttState.status === "completed" ? (
              <div className="max-w-2xl mx-auto">
                 {/* Re-use transcript card from App2 styling inside App structure */}
                 <div className={`rounded-3xl border p-6 space-y-4 text-center ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
                         <CheckCircle2 size={40} />
                    </div>
                    <h3 className="text-xl font-bold">Completed</h3>
                    <div className="rounded-2xl border bg-gray-50 dark:bg-[#0f172a] dark:border-gray-600 p-4 mt-4 text-left">
                       <textarea readOnly value={sttTranscript} rows={10} className="w-full bg-transparent outline-none text-sm resize-none" placeholder="Your transcript will appear here (If backed supported directly via status poll)..."/>
                    </div>
                    {sttDownloadUrl && (
                      <a href={sttDownloadUrl} className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700">
                        <Download className="h-4 w-4" /> Download transcript
                      </a>
                    )}
                    <button className="block w-full rounded-2xl bg-gray-200 dark:bg-gray-700 px-5 py-3 text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 mt-4" onClick={() => { setSttState(null); setSttJobId(null); }}>
                      {t('Create another')}
                    </button>
                 </div>
              </div>
            ) :
            <div className={`max-w-2xl mx-auto rounded-3xl p-6 lg:p-10 border ${isDarkMode ? 'bg-[#1e293b] border-gray-700 shadow-xl' : 'bg-white border-gray-100 shadow-xl'}`}>
              <h3 className="text-3xl font-bold mb-8">{t('Convert Speech to Text')}</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('Project Name')}</label>
                  <input type="text" value={sttName} onChange={e => setSttName(e.target.value)} placeholder={t('Transcription Name')} className={`w-full p-4 rounded-xl border outline-none font-medium ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`} />
                </div>
                <div className="relative">
                  <label className="block text-sm font-bold mb-2 block">{t('Target Language')}</label>
                  <div onClick={(e) => { e.stopPropagation(); setIsSttLangOpen(!isSttLangOpen); }} className={`w-full p-4 rounded-xl border flex justify-between items-center cursor-pointer ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`}><span className="text-sm font-medium">{sttLang}</span><ChevronDown size={16} className={`transition-transform ${isSttLangOpen ? 'rotate-180' : ''}`} /></div>
                  {isSttLangOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsSttLangOpen(false)}></div>
                      <div className={`absolute z-20 w-full mt-2 py-2 rounded-xl shadow-2xl border max-h-72 overflow-y-auto ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-white border-gray-200'}`}>
                        {apiLanguages.map(lang => (
                          <div key={lang} onClick={(e) => { e.stopPropagation(); setSttLang(lang); setIsSttLangOpen(false); }} className={`px-4 py-3 cursor-pointer hover:bg-blue-600 hover:text-white transition-colors ${sttLang === lang ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20' : ''}`}>{lang}</div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('Source Link (Optional)')}</label>
                  <div className={`flex items-center gap-3 w-full px-4 rounded-xl border ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                    <LinkIcon size={18} className="text-gray-400" />
                    <input type="text" value={sttLink} onChange={e => setSttLink(e.target.value)} placeholder={t('Paste YouTube URL')} className="flex-1 py-3.5 bg-transparent outline-none text-sm font-medium" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('Or Upload Audio / Video')}</label>
                  <label htmlFor="stt-upload" className={`block border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-all ${sttFile ? (isDarkMode ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50') : (isDarkMode ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50')}`}>
                    <input id="stt-upload" type="file" accept="audio/*,video/*" className="hidden" onChange={(e) => setSttFile(e.target.files[0])} />
                    {sttFile ? (
                      <><div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4 text-white"><Play size={28} fill="currentColor" /></div><p className="font-bold text-lg text-blue-600 dark:text-blue-400">{sttFile.name}</p></>
                    ) : (
                      <><Upload size={32} className="mb-3 text-gray-400" /><p className="font-bold text-lg">{t('Click or drag to upload')}</p><p className="text-sm text-gray-500 mt-2">Audio or Video format</p></>
                    )}
                  </label>
                </div>
                <button onClick={handleGenerateSTT} disabled={!sttName || (!sttFile && !sttLink.trim())} className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${sttName && (sttFile || sttLink.trim()) ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'}`}>
                  {t('Start Transcription')}
                </button>
              </div>
            </div>
          )}

          {/* AUDIOBOOK TAB */}
          {activeTab === 'Audiobook' && (
            abState && abState.status === "loading" ? renderRealGeneratingUI(abState, () => { setAbState(null); setAbJobId(null); }, 'Audiobooks') :
            <div className={`max-w-2xl mx-auto rounded-3xl p-6 lg:p-10 border ${isDarkMode ? 'bg-[#1e293b] border-gray-700 shadow-xl' : 'bg-white border-gray-100 shadow-xl'}`}>
              <h3 className="text-3xl font-bold mb-8">{t('Create Audiobook')}</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('Project Name')}</label>
                  <input type="text" value={audiobookName} onChange={e => setAudiobookName(e.target.value)} placeholder={t('Audiobook Name')} className={`w-full p-4 rounded-xl border outline-none font-medium ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`} />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('Upload Document')}</label>
                  <label htmlFor="audiobook-upload" className={`block border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-all ${audiobookFile ? (isDarkMode ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50') : (isDarkMode ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50')}`}>
                    <input id="audiobook-upload" type="file" accept={AUDIOBOOK_ACCEPT} className="hidden" onChange={(e) => setAudiobookFile(e.target.files[0])} />
                    {audiobookFile ? (
                      <><div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mb-3"><FileText size={28} className="text-green-600 dark:text-green-400" /></div><p className="font-bold text-green-600 dark:text-green-400">{audiobookFile.name}</p></>
                    ) : (
                      <><Upload size={32} className="mb-3 text-gray-400" /><p className="font-bold text-lg mb-1">{t('Click or drag to upload')}</p><p className="text-sm text-gray-500">.epub, .pdf, .txt, .html, .docx, .xml, .fdx</p></>
                    )}
                  </label>
                </div>
                <div className="relative">
                  <label className="text-sm font-bold mb-2 block">{t('Target Language')}</label>
                  <div onClick={(e) => { e.stopPropagation(); setIsAudiobookLangOpen(!isAudiobookLangOpen); }} className={`w-full p-4 rounded-xl border flex justify-between items-center cursor-pointer ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`}><span className="text-sm font-medium">{audiobookLang}</span><ChevronDown size={16} className={`transition-transform ${isAudiobookLangOpen ? 'rotate-180' : ''}`} /></div>
                  {isAudiobookLangOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsAudiobookLangOpen(false)}></div>
                      <div className={`absolute z-20 w-full mt-2 py-2 rounded-xl shadow-2xl border max-h-72 overflow-y-auto ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-white border-gray-200'}`}>
                        {apiLanguages.map(lang => (
                          <div key={lang} onClick={(e) => { e.stopPropagation(); setAudiobookLang(lang); setIsAudiobookLangOpen(false); }} className={`px-4 py-3 cursor-pointer hover:bg-blue-600 hover:text-white transition-colors ${audiobookLang === lang ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20' : ''}`}>{lang}</div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="relative">
                  <label className="text-sm font-bold mb-2 block flex justify-between">{t('Select Speaker')} <span onClick={(e) => { e.stopPropagation(); setActiveTab('Voices'); setVoicesTab('Explore'); }} className="text-blue-500 font-normal cursor-pointer text-xs hover:underline">{t('Explore')}</span></label>
                  <div onClick={(e) => { e.stopPropagation(); setIsAudiobookVoiceOpen(!isAudiobookVoiceOpen); }} className={`w-full p-4 rounded-xl border flex justify-between items-center cursor-pointer ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-gray-50 border-gray-300'}`}><span className="text-sm font-medium">{audiobookSelectedVoice}</span><ChevronDown size={16} className={`transition-transform ${isAudiobookVoiceOpen ? 'rotate-180' : ''}`} /></div>
                  {isAudiobookVoiceOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsAudiobookVoiceOpen(false)}></div>
                      <div className={`absolute z-20 w-full mt-2 py-2 rounded-xl shadow-2xl border max-h-72 overflow-y-auto ${isDarkMode ? 'bg-[#0f172a] border-gray-600' : 'bg-white border-gray-200'}`}>
                        {uniqueTTSVoices.map(voice => {
                          const isSaved = savedVoices.some(v => v.id === voice.id);
                          return (
                          <div key={voice.id} onClick={(e) => { e.stopPropagation(); setAudiobookSelectedVoice(voice.name); setIsAudiobookVoiceOpen(false); }} className={`px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-between group ${audiobookSelectedVoice === voice.name ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                            <div className="flex items-center gap-2"><p className="font-bold text-sm mb-0.5">{voice.name}</p>{isSaved && <Bookmark size={12} className="text-blue-500" fill="currentColor"/>}</div>
                          </div>
                        )})}
                      </div>
                    </>
                  )}
                </div>
                <button onClick={handleCreateAudiobook} disabled={!audiobookName || !audiobookFile} className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${audiobookName && audiobookFile ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'}`}>
                  {t('Generate Audiobook')}
                </button>
              </div>
            </div>
          )}

          {/* STUDIO (DRAFT OVERLAY) */}
          {activeTab === 'Studio' && (
            <div className={`max-w-6xl mx-auto rounded-3xl p-6 lg:p-10 border relative overflow-hidden ${isDarkMode ? 'bg-[#0f172a] border-gray-700 shadow-xl' : 'bg-white border-gray-100 shadow-xl'}`}>
              
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-30 flex flex-col items-center justify-center text-center p-6">
                 <Sparkles size={60} className="text-blue-500 mb-4"/>
                 <h3 className="text-4xl font-extrabold text-white mb-2">{t('AI Studio')} - Draft</h3>
                 <p className="text-xl text-gray-200">Preview only (Not yet active)</p>
              </div>

              <div className="opacity-50 pointer-events-none select-none">
                 <div className={`flex justify-between items-center p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-[#1a1f2e]' : 'bg-gray-100'}`}>
                    <div className="flex gap-2">
                      <div className="p-1 bg-gray-500/20 rounded"><ChevronLeft size={16} /></div>
                      <span className="text-sm font-medium">Project name</span>
                    </div>
                 </div>

                 <div className="flex gap-4 h-[300px] mb-4">
                    <div className="flex-1 bg-black rounded-lg border border-gray-800 flex items-center justify-center">
                       <Play size={40} className="text-gray-600"/>
                    </div>
                    <div className={`hidden lg:flex w-72 rounded-lg border p-4 flex-col gap-4 ${isDarkMode ? 'bg-[#1a1f2e] border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                       <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-black/50 border-gray-800' : 'bg-white border-gray-300'}`}>
                          <p className="text-sm font-bold">Assalomu alaykum</p>
                       </div>
                    </div>
                 </div>
                 <div className={`w-full h-32 rounded-lg border p-3 ${isDarkMode ? 'bg-[#1a1f2e] border-gray-800' : 'bg-gray-50 border-gray-200'}`}></div>
              </div>
            </div>
          )}

          {/* SETTINGS MAIN */}
          {activeTab === 'Settings' && settingsView === 'main' && (
             <div className={`max-w-4xl mx-auto rounded-3xl p-6 lg:p-10 border ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
                 <div className="flex justify-between items-center mb-8">
                     <h2 className="text-3xl font-bold">{t('Settings')}</h2>
                     <button onClick={() => setSettingsView('deleted')} className="px-5 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors rounded-xl text-sm font-bold flex items-center gap-2">
                         <Trash2 size={16}/> {t('Recently Deleted')}
                     </button>
                 </div>
                 
                 <div className="space-y-8">
                     <div className="space-y-6">
                       <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-6 border-b dark:border-gray-700 gap-4">
                         <div><p className="font-bold text-lg mb-1">{t('E-Mail Address')}</p><p className="text-gray-500 dark:text-gray-400">{userEmail || 'user@example.com'}</p></div>
                       </div>
                       <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-6 border-b dark:border-gray-700 gap-4">
                         <div><p className="font-bold text-lg mb-1">{t('Given Name')}</p><p className="text-gray-500 dark:text-gray-400">{userName}</p></div>
                         <button onClick={() => {
                            setNameModal({ isOpen: true, value: userName });
                         }} className={`px-4 py-2 border rounded-full font-bold text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>{t('Update Given Name')}</button>
                       </div>
                       <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-6 border-b dark:border-gray-700 gap-4">
                         <div><p className="font-bold text-lg mb-1">{t('Current Plan')}</p><p className="text-gray-500 dark:text-gray-400">Free</p></div>
                         <button onClick={() => setActiveTab('Subscription')} className={`px-4 py-2 border rounded-full font-bold text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>{t('Manage Subscription')}</button>
                       </div>
                     </div>

                     <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-6 border-b dark:border-gray-700 gap-4 relative">
                         <div><h3 className="font-bold text-lg mb-1">{t('Application language')}</h3><p className="text-gray-500 dark:text-gray-400">{appLang}</p></div>
                         <div className="relative">
                           <div onClick={(e) => { e.stopPropagation(); setIsAppLangOpen(!isAppLangOpen); }} className={`px-4 py-2 border rounded-full font-bold text-sm flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                               <Globe size={16}/> {appLang} <ChevronDown size={14}/>
                           </div>
                           {isAppLangOpen && (
                              <>
                                <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setIsAppLangOpen(false); }}></div>
                                <div className={`absolute right-0 top-full mt-2 w-56 max-h-64 overflow-y-auto rounded-xl shadow-xl border z-50 py-2 ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
                                  {allLanguages.map(lang => (
                                    <div key={lang} onClick={(e) => { e.stopPropagation(); changeAppLanguage(lang); }} className={`w-full text-base py-2.5 px-4 text-left cursor-pointer transition-colors ${appLang === lang ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                                      {lang}
                                    </div>
                                  ))}
                                </div>
                              </>
                           )}
                         </div>
                     </div>

                     <div className="flex flex-col sm:flex-row sm:items-center justify-between pt-4 gap-4">
                         <div className="flex-1 pr-8">
                             <h3 className="font-bold text-red-600 text-lg mb-2">{t('Delete Entire Account')}</h3>
                             <p className="text-gray-500 dark:text-gray-400 text-sm">{t('Permanently delete your entire account across all workspaces.')}</p>
                         </div>
                         <button className="px-6 py-2.5 border border-red-500 text-red-600 rounded-full font-bold hover:bg-red-50 dark:hover:bg-red-900/20 whitespace-nowrap transition-colors">{t('Delete Account')}</button>
                     </div>
                 </div>
             </div>
          )}

          {/* SETTINGS -> RECENTLY DELETED */}
          {activeTab === 'Settings' && settingsView === 'deleted' && (
             <div className={`max-w-4xl mx-auto rounded-3xl p-6 lg:p-10 border ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
                 <div className="flex items-center gap-4 mb-8">
                     <button onClick={() => setSettingsView('main')} className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 transition-colors"><ArrowLeft size={20}/></button>
                     <h2 className="text-3xl font-bold">{t('Recently Deleted')}</h2>
                 </div>
                 
                 <h3 className="text-xl font-bold mb-4 mt-8">{t('Files')}</h3>
                 {deletedFiles.length === 0 ? <p className="text-gray-500">{t('No deleted files.')}</p> : (
                     <div className="space-y-3">
                       {currentDeletedFiles.map((file, idx) => (
                           <div key={idx} className={`p-4 rounded-xl border flex items-center justify-between ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                               <div><p className="font-bold">{file.name}</p><p className="text-xs text-gray-500">{t(file.originalTab)} • {file.date}</p></div>
                               <button onClick={() => {
                                   setMyFiles(prev => ({ ...prev, [file.originalTab]: [...prev[file.originalTab], file] }));
                                   setDeletedFiles(prev => prev.filter(f => f.id !== file.id));
                               }} className="text-sm font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 transition-colors px-4 py-2 rounded-lg">{t('Recovery to use')}</button>
                           </div>
                       ))}
                       {maxDeletedFilesPage > 1 && (
                         <div className="flex items-center gap-2 mt-4 justify-center">
                            <button onClick={() => setDeletedFilesPage(prev => Math.max(0, prev - 1))} className={`w-8 h-8 rounded-lg border flex items-center justify-center ${isDarkMode ? 'border-gray-700 bg-[#1e293b]' : 'bg-white'}`}><ChevronLeft size={16}/></button>
                            <span className="text-sm font-bold">{deletedFilesPage + 1}</span>
                            <button onClick={() => setDeletedFilesPage(prev => Math.min(maxDeletedFilesPage - 1, prev + 1))} className={`w-8 h-8 rounded-lg border flex items-center justify-center ${isDarkMode ? 'border-gray-700 bg-[#1e293b]' : 'bg-white'}`}><ChevronRight size={16}/></button>
                         </div>
                       )}
                     </div>
                 )}

                 <h3 className="text-xl font-bold mb-4 mt-8">{t('Voices')}</h3>
                 {deletedVoices.length === 0 ? <p className="text-gray-500">{t('No deleted voices.')}</p> : (
                     <div className="space-y-3">
                       {currentDeletedVoices.map((voice, idx) => (
                           <div key={idx} className={`p-4 rounded-xl border flex items-center justify-between ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                               <div className="flex items-center gap-4">
                                  <div className={`w-10 h-10 rounded-full bg-gradient-to-tr ${voice.color} flex items-center justify-center text-white font-bold`}>{voice.name.charAt(0)}</div>
                                  <p className="font-bold">{voice.name}</p>
                               </div>
                               <button onClick={() => {
                                   setMyVoices(prev => [...prev, voice]);
                                   setDeletedVoices(prev => prev.filter(v => v.id !== voice.id));
                               }} className="text-sm font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 transition-colors px-4 py-2 rounded-lg">{t('Recovery to use')}</button>
                           </div>
                       ))}
                       {maxDeletedVoicesPage > 1 && (
                         <div className="flex items-center gap-2 mt-4 justify-center">
                            <button onClick={() => setDeletedVoicesPage(prev => Math.max(0, prev - 1))} className={`w-8 h-8 rounded-lg border flex items-center justify-center ${isDarkMode ? 'border-gray-700 bg-[#1e293b]' : 'bg-white'}`}><ChevronLeft size={16}/></button>
                            <span className="text-sm font-bold">{deletedVoicesPage + 1}</span>
                            <button onClick={() => setDeletedVoicesPage(prev => Math.min(maxDeletedVoicesPage - 1, prev + 1))} className={`w-8 h-8 rounded-lg border flex items-center justify-center ${isDarkMode ? 'border-gray-700 bg-[#1e293b]' : 'bg-white'}`}><ChevronRight size={16}/></button>
                         </div>
                       )}
                     </div>
                 )}
             </div>
          )}

          {/* USAGE ANALYZE */}
          {activeTab === 'Usage Analyze' && (
  <div
    className={`mx-auto max-w-4xl rounded-3xl border p-6 lg:p-10 ${
      isDarkMode ? 'border-gray-700 bg-[#1e293b]' : 'border-gray-200 bg-white'
    }`}
  >
    <h2 className="mb-6 text-2xl font-bold">
      {t('Your Usage & Credit Limit Details')}
    </h2>

    <div
      className={`mb-10 overflow-hidden rounded-xl border ${
        isDarkMode ? 'border-gray-700' : 'border-gray-200'
      }`}
    >
      <table className="w-full text-left">
        <thead className={`text-sm ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <tr>
            <th className="p-4 font-semibold">{t('Limit Name')}</th>
            <th className="p-4 font-semibold">{t('Usage')}</th>
            <th className="p-4 font-semibold text-right">{t('Limit')}</th>
          </tr>
        </thead>
        <tbody className="divide-y dark:divide-gray-700">
          <tr>
            <td className="p-4 text-lg font-bold">{t('Workspace')}</td>
            <td className="p-4 text-lg font-medium">
              <span className="block text-blue-500">{Number(credits || 0).toLocaleString()} credits</span>
              <span className="block">{Number(minutes || 0)} mins</span>
            </td>
            <td className="p-4 text-right text-lg font-bold">
              <span className="block">10,000 credits</span>
              <span className="block">120 mins</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="mb-6 flex items-center justify-between gap-4">
      <h3 className="text-xl font-bold">{t('Request Logs')}</h3>

      <select
        value={usageFilter}
        onChange={(e) => {
          setUsageFilter(e.target.value);
          setUsagePage(0);
        }}
        className={`rounded-lg border p-2 text-sm font-bold outline-none ${
          isDarkMode
            ? 'border-gray-600 bg-[#0f172a]'
            : 'border-gray-300 bg-gray-50'
        }`}
      >
        <option value="All">{t('All Requests')}</option>
        <option value="Text to Speech">{t('Text to Speech')}</option>
        <option value="Dubbing AI">{t('Dubbing AI')}</option>
        <option value="Speech to Text">{t('Speech to Text')}</option>
        <option value="Voice Cloning">{t('Voice Cloning')}</option>
        <option value="Audiobook">{t('Audiobook')}</option>
      </select>
    </div>

    {requestsLog.length === 0 ? (
      <p className="text-gray-500">{t('No files found.')}</p>
    ) : currentLogs.length === 0 ? (
      <p className="text-gray-500">{t('No files found.')}</p>
    ) : (
      <div className="space-y-3">
        {currentLogs.map((log) => (
          <div
            key={log.id}
            className={`flex items-center justify-between rounded-xl border p-4 ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}
          >
            <div>
              <p className="font-bold">{t(log.type)}</p>
              <p className="mt-0.5 text-xs font-medium text-gray-500">
                {log.date} at {log.time}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-red-500">-{log.cost}</span>
              <span className="rounded-md bg-gray-100 px-3 py-1 text-xs font-bold dark:bg-gray-800">
                {t('Success')}
              </span>
            </div>
          </div>
        ))}

        {maxUsagePage > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={() => setUsagePage((prev) => Math.max(0, prev - 1))}
              className={`flex h-8 w-8 items-center justify-center rounded-lg border ${
                isDarkMode ? 'border-gray-700 bg-[#1e293b]' : 'bg-white'
              }`}
            >
              <ChevronLeft size={16} />
            </button>

            <span className="text-sm font-bold">{usagePage + 1}</span>

            <button
              onClick={() => setUsagePage((prev) => Math.min(maxUsagePage - 1, prev + 1))}
              className={`flex h-8 w-8 items-center justify-center rounded-lg border ${
                isDarkMode ? 'border-gray-700 bg-[#1e293b]' : 'bg-white'
              }`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    )}
  </div>
)}

          {/* SUBSCRIPTION */}
          {activeTab === 'Subscription' && (
            <div className={`max-w-4xl mx-auto rounded-3xl p-6 lg:p-10 border ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}>
                <h2 className="text-2xl font-bold mb-6">{t('Subscription')}</h2>
                
                <div className={`p-8 rounded-2xl border-2 ${isDarkMode ? 'bg-[#0f172a] border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 border-b pb-6 dark:border-gray-700">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-500 mb-1">{t('Free Plan')}</h3>
                            <div className="text-4xl font-bold">$0 <span className="text-lg text-gray-400 font-normal">{t('/mo')}</span></div>
                        </div>
                        <button 
                            onClick={() => {
                                if(onNavigateToPricing) onNavigateToPricing();
                            }} 
                            className="mt-4 md:mt-0 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-md">
                            {t('Upgrade Plan')}
                        </button>
                    </div>

                    <h4 className="font-bold text-lg mb-4">{t('Benefits')}</h4>
                    <ul className="space-y-4 text-sm font-medium">
                        <li className="flex items-center gap-3"><CheckCircle2 className="text-green-500 shrink-0" size={20} /> <span>{t('10 mins Dubbing AI/mo')}</span></li>
                        <li className="flex items-center gap-3"><CheckCircle2 className="text-green-500 shrink-0" size={20} /> <span>{t('10,000 credits/mo')}</span></li>
                        <li className="flex items-center gap-3"><CheckCircle2 className="text-green-500 shrink-0" size={20} /> <span>{t('80+ languages')}</span></li>
                        <li className="flex items-center gap-3"><CheckCircle2 className="text-green-500 shrink-0" size={20} /> <span>{t('Standard audio quality')}</span></li>
                        <li className="flex items-center gap-3"><CheckCircle2 className="text-green-500 shrink-0" size={20} /> <span>{t('Attribution required')}</span></li>
                    </ul>
                </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default App;